import { createContext, useContext, useState, useCallback } from "react";

const translations = {
  zh: {
    // Nav
    navTitle: "統合分析",
    navTitleSuffix: "101",
    navWhat: "概念",
    navWhy: "意義",
    navDemo: "演示",
    navHow: "方法",
    navPlots: "圖表",
    navQuiz: "彩蛋",
    langSwitch: "EN",

    // Hero
    heroBadge: "初學者指南",
    heroTitle1: "到底什麼是",
    heroTitle2: "統合分析",
    heroTitle3: "？",
    heroDesc: "想像一下，閱讀某個主題的每一項研究，然後用數學方法將它們組合起來找到真正的答案。這就是統合分析——比你想像的要簡單得多。",
    heroBtn: "開始學習 →",

    // What section
    whatLabel: "基礎知識",
    whatTitle: "統合分析是什麼？",
    whatIntro: "想像一下：你想知道一種新藥是否有效。你找到了20項研究——有的說有效，有的說無效，有的說可能有效。與其只挑選你最喜歡的那一項，統合分析會將全部20項結果進行數學組合，得出一個更可靠的答案。就像向20位專家提問，然後計算群體共識，給予最有把握的專家更大的權重。",
    whatCard1Title: "這是數學，不是觀點",
    whatCard1Text: "與作者用自己的話進行總結的傳統文獻綜述不同，統合分析使用統計公式來計算精確的合併結果。",
    whatCard2Title: "並非所有研究都平等",
    whatCard2Text: "更大、更精確的研究擁有更多影響力。一項納入1000名患者的研究比僅有20名患者的研究更有份量——就像你更信任樣本量更大的民意調查一樣。",
    whatCard3Title: "更大過程的一部分",
    whatCard3Text: "統合分析存在於「系統性綜論」之中——這是一個用於查找、評估和合併某個問題所有相關研究的結構化過程。",
    whatAnalogyTitle: "餐廳評價類比",
    whatAnalogyText: "你會只根據一條評價來選餐廳嗎？大概不會——你會閱讀很多評價，形成一個整體印象。但你會更看重去過5次的人的評價，而不是只路過的人。統合分析對科學研究所做的正是如此：閱讀所有研究，按質量和規模加權，然後給你最終結論。",

    // Why section
    whyLabel: "研究意義",
    whyTitle: "為什麼要做統合分析？",
    whyIntro: "單個研究就像拼圖碎片——本身很有價值，但你需要把它們放在一起才能理解全貌。",
    whyCard1Title: "更強的統計效力",
    whyCard1Text: "小型研究往往無法檢測到真實效果。透過匯集數據，統合分析實現了相當於更大研究的統計效力——有時達數萬名參與者。",
    whyCard2Title: "解決分歧",
    whyCard2Text: "當研究A說「是」而研究B說「否」時，統合分析提供了公平的、數學化的仲裁，而不是選擇性引用。",
    whyCard3Title: "發現隱藏模式",
    whyCard3Text: "透過次族群分析，你可以發現一種治療對老年患者有效但對年輕患者無效——這是單一研究可能無法揭示的。",
    whyCard4Title: "影響真實決策",
    whyCard4Text: "統合分析位於證據金字塔的頂端。政府、醫院和指南委員會依靠它們來制定治療和政策決策。",
    whyCard5Title: "揭露缺失證據",
    whyCard5Text: "漏斗圖等工具可以揭示陰性結果的研究是否未被發表——這種現象稱為發表偏倚。",
    whyCard6Title: "指引未來研究",
    whyCard6Text: "透過揭示我們已知和未知的內容，統合分析突出研究空白，指導最需要新研究的方向。",

    // Combiner
    combinerLabel: "實際演示",
    combinerTitle: "觀看研究如何合併",
    combinerDesc: "這個互動演示展示了統合分析的工作原理。八項研究各有不同結果——點擊「合併」查看按樣本量加權如何產生一個更可信的答案。",
    combinerScattered: "八項研究，八個不同答案",
    combinerCombining: "正在合併證據……",
    combinerCombined: "合併後的估計值",
    combinerScatteredDesc: "每個圓點是一項研究。它們結果不一——你信任哪一個？",
    combinerCombiningDesc: "按樣本量加權以找出真實訊號……",
    combinerCombinedDesc: (pooled) => `合併效應值：${pooled}——較大的研究將估計值拉向小的正效應`,
    combinerNegative: "← 負效應",
    combinerPositive: "正效應 →",
    combinerNoEffect: "無效應",
    combinerCombineBtn: "合併研究 →",
    combinerResetBtn: "↺ 重置",

    // Study descriptions
    studyADesc: "小型隨機對照試驗——發現中等正效應",
    studyBDesc: "大型試驗——發現幾乎無效應",
    studyCDesc: "小型研究——發現強效應",
    studyDDesc: "最大型試驗——發現小的正效應",
    studyEDesc: "中型研究——發現中等效應",
    studyFDesc: "中型試驗——發現負效應",
    studyGDesc: "大型試驗——發現幾乎無效應",
    studyHDesc: "中型研究——發現中等正效應",

    // How section
    howLabel: "分步指南",
    howTitle: "如何進行統合分析",
    howDesc: "這是一個結構化的8步流程。點擊每個步驟展開詳情，查看生動的類比幫助理解。",
    howNote: "基於PRISMA 2020指南和已建立的方法學框架",
    howThinkOfIt: "💡 可以這樣理解：",

    step1Title: "確定研究問題",
    step1Analogy: "就像在去買菜之前先確定你要做什麼菜。",
    step1Detail1: "使用PICO等結構化框架：人群(Population)、干預(Intervention)、對照(Comparison)、結果(Outcome)。",
    step1Detail2: "你的問題決定了一切——納入哪些研究、提取哪些數據，以及如何分析。",
    step1Detail3: "確保你的主題上有足夠的研究來支持定量合成。",
    step1Detail4: "在開始之前，撰寫詳細的計畫書並進行註冊（如在PROSPERO上註冊）。",

    step2Title: "系統檢索文獻",
    step2Analogy: "就像在多個海域撒下大網，確保不漏掉任何一條魚。",
    step2Detail1: "檢索多個資料庫：PubMed、Embase、Cochrane圖書館以及其他與你領域相關的資料庫。",
    step2Detail2: "使用精心構建的關鍵詞組合和布林運算符（AND、OR、NOT）。",
    step2Detail3: "不要忘記「灰色文獻」——學位論文、會議摘要、預印本——以減少偏倚。",
    step2Detail4: "仔細記錄每次檢索。你需要在PRISMA流程圖中報告這些。",

    step3Title: "篩選和選擇研究",
    step3Analogy: "就像從捕獲的魚中挑選出符合你需要的那些。",
    step3Detail1: "將預定義的納入排除標準應用於找到的每項研究。",
    step3Detail2: "至少兩名審稿人應獨立篩選標題、摘要，然後是全文。",
    step3Detail3: "使用參考文獻管理工具（EndNote、Mendeley）和篩選軟體（Rayyan、Covidence）。",
    step3Detail4: "準確記錄每項被排除研究的原因——透明度至關重要。",

    step4Title: "評估品質和偏倚風險",
    step4Analogy: "就像在烹飪前檢查每種食材的新鮮度和品質。",
    step4Detail1: "使用經過驗證的工具：Cochrane偏倚風險工具用於RCT，Newcastle-Ottawa量表用於觀察性研究。",
    step4Detail2: "評估隨機化、盲法、數據不完整和選擇性報告等方面。",
    step4Detail3: "低品質研究可能扭曲整體結果——你可能需要進行排除它們的敏感性分析。",
    step4Detail4: "兩名獨立審稿人應評估品質，並有解決分歧的程序。",

    step5Title: "提取數據和計算效應量",
    step5Analogy: "就像在加入鍋中之前精確測量每種配料。",
    step5Detail1: "使用標準化表格記錄樣本量、均值、標準差和事件數。",
    step5Detail2: "選擇效應量指標：二分類結果用比值比(OR)，連續性指標用均數差(MD)。",
    step5Detail3: "每項研究被編碼為一個數字（效應量）加上一個精確度度量。",
    step5Detail4: "仔細核實提取的數據——即使已發表的統合分析也被發現存在錯誤。",

    step6Title: "合併結果和分析",
    step6Analogy: "就像將所有精確測量的配料混合在一起，看看做出了什麼菜。",
    step6Detail1: "選擇模型：固定效應（假設存在一個真實效應）vs 隨機效應（假設效應在研究間變化）。",
    step6Detail2: "每項研究按精確度加權——更大、更精確的研究有更多影響力。",
    step6Detail3: "用I²評估異質性：低於25%為低，25-50%為中等，高於50%為高。",
    step6Detail4: "如果異質性高，透過次族群分析或統合迴歸探索原因。",

    step7Title: "檢查發表偏倚",
    step7Analogy: "就像確保你的食譜書沒有缺頁——尤其是那些失敗食譜的頁面。",
    step7Detail1: "製作漏斗圖：研究效應值 vs 精確度。對稱性表明無偏倚。",
    step7Detail2: "使用Egger檢驗或Begg檢驗來統計量化潛在偏倚。",
    step7Detail3: "剪補法可以估計可能「缺失」了多少研究並調整結果。",
    step7Detail4: "請記住：漏斗圖不對稱也可能反映真正的異質性，而不僅僅是偏倚。",

    step8Title: "報告研究結果",
    step8Analogy: "就像寫下食譜，讓任何人都能精確地重現你的菜餚。",
    step8Detail1: "遵循PRISMA 2020指南——一份涵蓋綜述各個方面的27項清單。",
    step8Detail2: "包含PRISMA流程圖，顯示找到、篩選和納入了多少研究。",
    step8Detail3: "在森林圖中展示所有關鍵統計數據：效應量、信賴區間、I²、P值。",
    step8Detail4: "公開討論局限性：納入研究的品質、潛在偏倚和可推廣性。",

    // Forest plot
    forestLabel: "關鍵視覺化",
    forestTitle: "如何閱讀森林圖",
    forestDesc: "森林圖是統合分析的標誌性視覺化工具。每個元素都有其含義——點擊下方不同部分了解它們代表什麼。",
    forestNote: "在這個例子中，研究正在測試一種治療是否能減少不良結果。左側的值表示治療有效。",
    forestClickHint: "👆 點擊下方的方塊、線段、中心線或菱形",
    forestFavorsTreatment: "← 有利於治療",
    forestFavorsControl: "有利於對照 →",
    forestOverall: "總體",
    forestSquareTitle: "研究效應值（方塊）",
    forestSquareText: "每個方塊代表一項研究的結果。其大小反映該研究的權重——更大的方塊意味著樣本量更大、更精確的研究，對最終結果貢獻更大。",
    forestLineTitle: "信賴區間（線段）",
    forestLineText: "水平線段顯示合理值的範圍。線段越短=精確度越高。如果它穿過中心線，則該結果本身在統計學上不顯著。",
    forestCenterTitle: "無效線",
    forestCenterText: "這條豎線代表零效應——兩組之間沒有差異。左側的研究有利於治療組；右側的研究有利於對照組。",
    forestDiamondTitle: "合併估計值（菱形）",
    forestDiamondText: "菱形是所有研究的合併結果。其寬度顯示信賴區間。如果整個菱形位於中心線的一側，則總體效應在統計學上是顯著的。",

    // Glossary
    glossaryEffectSize: "效應量",
    glossaryEffectSizeDef: "衡量兩組之間差異大小的數字。常見類型：比值比、風險比和標準化均數差。",
    glossaryCI: "信賴區間",
    glossaryCIDef: "真實效應可能落入的範圍。95%CI意味著我們有95%的把握認為真實答案在這個範圍內。",
    glossaryI2: "I²（異質性）",
    glossaryI2Def: "研究間變異中有多少百分比是真實的（而非隨機雜訊）。低於25%=低，高於75%=非常高。",
    glossaryFunnel: "漏斗圖",
    glossaryFunnelDef: "用於檢查發表偏倚的散佈圖。研究應對稱分佈——如果不對稱，可能有缺失的結果。",
    glossaryFixedRandom: "固定效應 vs 隨機效應",
    glossaryFixedRandomDef: "固定效應假設存在一個真實效應。隨機效應假設效應在研究間變化。隨機效應更常用，也更保守。",
    glossaryPRISMA: "PRISMA",
    glossaryPRISMADef: "確保你的統合分析透明、可重複和完整的27項報告清單。於2020年更新。",

    // Egg Hunt UI
    eggHuntLabel: "自測",
    eggHuntTitle: "蒐集彩蛋",
    eggHuntDesc: "找到7個隱藏的彩蛋，測試你的統合分析知識！每個正確答案都能收集一個彩蛋到你的籃子裡。",
    eggHuntStart: "開始蒐集 →",
    eggHuntProgress: (current) => `第${current}個蛋，共7個`,
    eggHuntCorrect: (name) => `你找到了${name}！`,
    eggHuntWrong: "蛋碎了……",
    eggHuntNext: "下一個蛋 →",
    eggHuntResults: "查看你的籃子 →",
    eggHuntBasketTitle: "你的彩蛋籃",
    eggHuntScore: (n) => `你找到了${n}個蛋！`,
    eggHuntMaster: "統合分析大師",
    eggHuntExplorer: "統合分析探索者",
    eggHuntApprentice: "統合分析學徒",
    eggHuntKeepTrying: "繼續探索！",
    eggHuntDownload: "下載速查表",
    eggHuntPlayAgain: "再玩一次 →",
    eggHuntAllSheets: "下載綜合速查表",

    // Egg category names
    eggCatDiscovery: "探索蛋",
    eggCatData: "數據蛋",
    eggCatForest: "森林蛋",
    eggCatVariety: "多樣蛋",
    eggCatSearch: "搜索蛋",
    eggCatBias: "偏倚蛋",
    eggCatWisdom: "智慧蛋",

    // Cheat sheet names
    eggSheetDiscovery: "什麼是統合分析？",
    eggSheetData: "數據提取袖珍指南",
    eggSheetForest: "如何閱讀森林圖",
    eggSheetVariety: "異質性速查表",
    eggSheetSearch: "搜索與篩選指南",
    eggSheetBias: "偏倚偵探卡",
    eggSheetWisdom: "解讀指南針",

    footerText: "內容綜合自領先的方法學指南，包括PRISMA 2020、Cochrane手冊和經過同儕審查的統合分析教程。本站為面向初學者的教育資源。",
    footerBrand: "統合分析 101",

    // ═══ COURSE HUB ═══
    hubNavTitle: "統合分析",
    hubNavSuffix: "101",
    navCourses: "課程選單",
    navAbout: "關於本站",
    navLogin: "登入",
    navLogout: "登出",
    navLoginSave: "登入以儲存進度",
    navProfile: "我的進度",

    // About page
    aboutHeroTitle: "關於統合分析 101",
    aboutHeroDesc: "一個專為藥師與臨床研究人員設計的互動式學習平台，從零開始教你統合分析。",
    aboutGoalTitle: "我們的目標",
    aboutGoalText: "讓統合分析不再令人望而卻步。透過互動圖表和趣味小遊戲，我們將複雜的方法學概念拆解成任何人都能理解的內容。",
    aboutAudienceTitle: "適合誰？",
    aboutAudienceText: "主要面向沒有研究背景的藥師——但任何對實證醫學有興趣的醫療專業人員都歡迎。不需要統計學基礎，我們會從最基本的概念開始。",
    aboutStructureTitle: "課程架構",
    aboutStructureText: "共 6 堂循序漸進的課程，分為兩個部分。基礎課程涵蓋規劃與準備（概念、PICO、文獻搜尋、數據擷取與偏倚風險評估），進階課程涵蓋分析與詮釋（效應量、森林圖、異質性、發表偏倚）。兩階段之間各有一個 AI 輔助的研究工作坊，帶你實際操作完整流程。每堂課後都有恐龍主題的小遊戲！",
    aboutSourcesTitle: "參考來源",
    aboutSourcesText: "內容綜合自 PRISMA 2020、Cochrane 手冊以及經過同儕審查的統合分析教程。所有教學內容皆經過仔細校對以確保正確性。",
    aboutBuiltWith: "技術架構",
    aboutBackToHub: "回到課程總覽",

    // Profile page
    profileLoginRequired: "請先登入",
    profileLoginDesc: "登入 Google 帳號後即可查看你的學習進度、蒐集的恐龍和遊戲成績。",
    profileOverview: "進度總覽",
    profileEggs: "蒐集彩蛋",
    profileHatched: "孵化恐龍",
    profileFed: "餵食恐龍",
    profileSaved: "拯救家園",
    profileKeys: "取得鑰匙",
    profileEscaped: "成功逃脫",
    profileDinoCollection: "恐龍圖鑑",
    profileBestScores: "最佳成績",
    profileGame0: "彩蛋蒐集",
    profileGame1: "龍蛋孵化",
    profileGame2: "食物救援",
    profileGame3: "拯救家園",
    profileGame4: "鑰匙探索",
    profileGame5: "地圖逃脫",
    profileEmpty: "你還沒有任何遊戲記錄。開始學習來蒐集恐龍吧！",
    profileStartLearning: "開始學習 →",
    hubBadge: "學習平台",
    hubTitle1: "從零到精通",
    hubTitle2: "統合分析",
    hubDesc: "從基礎概念到實戰操作，6 堂循序漸進的課程帶你完整學會如何進行統合分析。每堂課後都有小遊戲鞏固學習！",
    hubPrecourse: "先修課程",
    hubPrecourseDesc: "統合分析概論",
    hubSection1: "基礎課程",
    hubSection1Desc: "Course 1-3 · PICO、文獻搜尋、數據擷取",
    hubSection2: "進階課程",
    hubSection2Desc: "Course 4-5 · 效應量、森林圖、異質性、發表偏倚",
    hubMidtermTitle: "研究工作坊：規劃篇",
    hubMidtermDesc: "定義研究問題、搜尋文獻、擷取資料、評估偏倚風險",
    hubFinalTitle: "研究工作坊：分析篇",
    hubFinalDesc: "選擇分析方法、執行統合分析、撰寫結論",
    hubC0Title: "什麼是統合分析？",
    hubC0Desc: "了解統合分析的基本概念、為什麼要做、以及分步流程。",
    hubC0Game: "彩蛋蒐集遊戲",
    hubC1Title: "打造研究問題 (PICO)",
    hubC1Desc: "學會用 PICO/PICOS 架構把模糊的想法變成精準的研究問題。",
    hubC1Game: "恐龍孵化挑戰",
    hubC2Title: "文獻搜尋與篩選 (PRISMA)",
    hubC2Desc: "系統性地搜尋文獻庫，用 PRISMA 流程圖記錄篩選過程。",
    hubC2Game: "恐龍食物救援",
    hubC3Title: "數據擷取與偏倚風險評估",
    hubC3Desc: "從納入的研究中提取數據，並評估每項研究的品質。",
    hubC3Game: "拯救恐龍的家",
    hubC4Title: "效應量與森林圖",
    hubC4Desc: "計算效應量、理解加權方法、學會閱讀和製作森林圖。",
    hubC4Game: "恐龍鑰匙探索",
    hubC5Title: "異質性與發表偏倚",
    hubC5Desc: "評估研究間的差異、檢測發表偏倚、掌握 PRISMA 報告要點。",
    hubC5Game: "恐龍地圖大逃脫",
    hubComingSoon: "即將推出",
    hubFooter: "統合分析 101 — 從基礎到進階的完整學習路徑",

    // ═══ COURSE 1: PICO ═══
    c1Label: "課程 1",
    c1Title: "打造你的研究問題",
    c1Subtitle: "PICO / PICOS 架構",
    c1Desc: "好的統合分析始於好的問題。學會用 PICO 架構把模糊的想法變成精準、可搜尋、可回答的研究問題。",
    c1StartBtn: "開始學習 →",

    c1s1Label: "為什麼需要 PICO？",
    c1s1Title: "模糊的問題，得到模糊的答案",
    c1s1Intro: "想像你走進圖書館問：「有沒有關於心臟的書？」管理員會不知所措。但如果你問：「有沒有比較阿斯匹靈和安慰劑預防心肌梗塞的隨機試驗？」這就容易找了。",
    c1s1Bad: "❌ 不好的問題",
    c1s1BadEx: "「糖尿病藥物有效嗎？」",
    c1s1BadWhy: "哪種藥？對誰？跟什麼比？量什麼結果？太模糊了。",
    c1s1Good: "✅ 好的 PICO 問題",
    c1s1GoodEx: "「在第二型糖尿病成人患者(P)中，使用 Metformin(I)相比安慰劑(C)，能否降低 HbA1c(O)？」",
    c1s1GoodWhy: "每個元素都清楚定義，可以直接用來搜尋文獻。",
    c1s1Next: "接下來認識 PICO 的四個元素 →",

    c1s2Label: "PICO 的四個元素",
    c1s2Title: "拆解 PICO",
    c1s2Intro: "PICO 是四個英文字的縮寫，每一個都幫你鎖定研究問題的一個面向：",
    c1pTitle: "P — Population 族群",
    c1pDesc: "你要研究的對象是誰？越具體越好。",
    c1pExample: "「65歲以上、有高血壓病史的成年患者」",
    c1pTip: "想想：年齡、性別、疾病階段、嚴重度、地區",
    c1iTitle: "I — Intervention 介入措施",
    c1iDesc: "你想測試的治療、藥物、策略或暴露因子是什麼？",
    c1iExample: "「每日口服 Amlodipine 5mg」",
    c1iTip: "想想：劑量、頻率、給藥途徑、持續時間",
    c1cTitle: "C — Comparison 對照組",
    c1cDesc: "你拿什麼來做比較？可以是安慰劑、其他藥物、或不治療。",
    c1cExample: "「安慰劑」或「Lisinopril 10mg」",
    c1cTip: "沒有對照 = 無法判斷效果。這是因果推論的核心。",
    c1oTitle: "O — Outcome 結果指標",
    c1oDesc: "你要量測什麼來判斷成敗？",
    c1oExample: "「收縮壓變化 (mmHg)」或「心血管事件發生率」",
    c1oTip: "主要結果 vs 次要結果都要想清楚",
    c1s2Next: "學會了四個元素，來看 PICOS 的延伸 →",

    c1s3Label: "進階：PICOS",
    c1s3Title: "加上 S — 研究設計",
    c1s3Intro: "有時候你需要第五個字母：S (Study design)。它幫你限定要納入哪種類型的研究。",
    c1sTitle: "S — Study Design 研究設計",
    c1sDesc: "你要納入哪種研究？這會影響你的搜尋範圍和證據品質。",
    c1sOptions: "常見選擇：",
    c1sRCT: "隨機對照試驗 (RCT) — 證據等級最高",
    c1sCohort: "世代研究 (Cohort) — 觀察性，適合長期追蹤",
    c1sCaseControl: "病例對照研究 — 適合罕見疾病",
    c1sAny: "所有研究設計 — 當 RCT 數量不足時",
    c1s3Tip: "如果你的統合分析只想納入 RCT，在 S 這裡就要寫清楚。這會直接影響你在 Course 2 搜尋文獻時的篩選標準。",
    c1s3Next: "先看看常見錯誤 →",

    c1s4Label: "互動練習",
    c1s4Title: "選出最佳 PICO",
    c1s4Intro: "選擇一個臨床情境，然後為 P、I、C、O 每個元素挑選最恰當的定義。每一步都有即時回饋，幫你理解什麼才是好的 PICO。",
    c1s4Scenario: "選擇一個情境開始練習：",
    c1scenarioA: "🫀 心血管：SGLT2 抑制劑治療心衰竭",
    c1scenarioB: "🦠 感染科：延長輸注 β-lactam 抗生素",
    c1scenarioC: "🧠 精神科：CBT 治療重度憂鬱症",
    c1s4YourP: "你的 P (族群)：",
    c1s4YourI: "你的 I (介入)：",
    c1s4YourC: "你的 C (對照)：",
    c1s4YourO: "你的 O (結果)：",
    c1s4PlaceholderP: "例如：65歲以上心衰竭患者...",
    c1s4PlaceholderI: "例如：Dapagliflozin 10mg QD...",
    c1s4PlaceholderC: "例如：安慰劑...",
    c1s4PlaceholderO: "例如：住院率...",
    c1s4ShowAnswer: "看看參考答案",
    c1s4HideAnswer: "隱藏參考答案",
    c1s4RefTitle: "參考 PICO",
    c1scenarioARef: { p: "射出分率降低的心衰竭 (HFrEF) 成年患者，NYHA class II-IV", i: "SGLT2 抑制劑 (如 Dapagliflozin 10mg 或 Empagliflozin 10mg) 加上標準治療", c: "安慰劑加上標準治療", o: "主要：心血管死亡或心衰竭住院的複合終點；次要：全因死亡率、生活品質 (KCCQ)" },
    c1scenarioBRef: { p: "加護病房中接受 β-lactam 抗生素治療的重症成人患者", i: "延長輸注 (≥3小時持續輸注或間歇延長輸注)", c: "傳統間歇輸注 (30分鐘 bolus)", o: "主要：院內死亡率；次要：臨床治癒率、ICU 住院天數" },
    c1scenarioCRef: { p: "符合 DSM-5 重度憂鬱症診斷標準的成年患者 (18歲以上)", i: "認知行為治療 (CBT) + 標準藥物治療 (SSRI)，每週 1 次，持續 12-16 週", c: "僅標準藥物治療 (SSRI)", o: "主要：憂鬱量表分數變化 (PHQ-9 或 HAM-D)；次要：緩解率、復發率" },
    c1s4Next: "來動手練習！ →",

    c1s5Label: "常見錯誤",
    c1s5Title: "PICO 的五大陷阱",
    c1s5Intro: "初學者最常犯的錯誤——避開這些陷阱，你的研究問題就成功了一半。",
    c1trap1Title: "族群太廣泛", c1trap1Bad: "「所有糖尿病患者」", c1trap1Good: "「第二型糖尿病、HbA1c > 7%、未使用胰島素的成人」",
    c1trap2Title: "沒有明確的對照組", c1trap2Bad: "「Metformin 有效嗎？」", c1trap2Good: "「Metformin vs 安慰劑」或「Metformin vs Sulfonylurea」",
    c1trap3Title: "結果指標太模糊", c1trap3Bad: "「病人有沒有好轉？」", c1trap3Good: "「HbA1c 在 12 週後的平均變化量」",
    c1trap4Title: "問題太大無法回答", c1trap4Bad: "「所有降血壓藥對所有心血管疾病的效果」", c1trap4Good: "「ACE抑制劑 vs ARB 對高血壓患者中風風險的影響」",
    c1trap5Title: "忘記考慮可行性", c1trap5Bad: "設定了完美 PICO 但只找到 2 篇相關研究", c1trap5Good: "先初步搜尋確認有足夠研究，再確定 PICO",
    c1s5Next: "前往龍蛋孵化挑戰！ 🥚🔥",

    c1gameLabel: "龍蛋孵化挑戰",
    c1gameTitle: "你能孵出小恐龍嗎？",
    c1gameDesc: "選擇一顆龍蛋，回答 7 道 PICO 問題。每答對一題，龍蛋就會獲得陽光，裂開一些！但每答錯一題，龍蛋會被寒冰覆蓋——錯 3 題龍蛋就會被凍住……",
    c1gameStart: "開始孵化 🥚",
    c1gameQ: (n) => `第 ${n} 題 / 共 7 題`,
    c1gameCorrect: "正確！龍蛋裂開了一些！ 🔥",
    c1gameWrong: "答錯了……龍蛋感到一陣寒意 ❄️",
    c1gameNext: "下一題 →",
    c1gameResults: "查看結果 →",
    c1gameHatched: "恭喜！龍蛋孵化成功！ 🎉",
    c1gameHatchedDesc: "你的小恐龍誕生了！牠們每一隻都代表你對 PICO 的精熟掌握。",
    c1gameFrozen: "龍蛋被凍住了… ❄️",
    c1gameFrozenDesc: "別灰心！回去複習 PICO 的內容，再來挑戰一次吧！",
    c1gameScore: (s) => `☀️ 蒐集到 ${s} 個陽光`,
    c1gameRetry: "再挑戰一次 🔄",
    c1gameContinue: "前往 AI 工作坊 →",

    c1dinoNames: ["翠牙龍", "蒼瀾龍", "金翼龍", "焰角龍", "紫棘龍", "珀爪龍", "鐵穹龍"],

    // AI PICO Workshop
    c1aiLabel: "AI 練習工坊",
    c1aiTitle: "讓 AI 幫你檢查 PICO",
    c1aiDesc: "選擇一個臨床情境，寫出 PICO，然後讓 AI 逐一檢查每個元素，給你即時的個人化回饋。填完全部四個欄位後，還可以做整體評估！",
    c1aiScenario: "選擇情境：",

    // AI Freestyle PICO
    c1freeLabel: "自由練習",
    c1freeTitle: "用你自己的主題練習 PICO",
    c1freeDesc: "已經有想研究的題目了嗎？寫下你的研究主題，AI 會幫你檢查主題是否合適，然後逐一評估你的 PICO。這是從課堂練習到真實研究的第一步！",
    c1freeTopic: "你的研究主題：",
    c1freeTopicHint: "用一兩句話描述你想探討的臨床問題，例如：「我想研究 probiotics 能不能預防 ICU 病人的 C. diff 感染」",
    c1freeTopicPlaceholder: "例如：SGLT2 抑制劑對慢性腎病進展的效果...",
    c1freeTopicCheck: "AI 檢查主題",
    c1freeNowPico: "現在寫下你的 PICO",
    c1freePlaceholderP: "針對你的主題，定義研究族群...",
    c1freePlaceholderI: "你要測試的介入措施...",
    c1freePlaceholderC: "對照組是什麼...",
    c1freePlaceholderO: "你要測量的結果指標...",

    // ─── Shared workshop UI strings (used by AIPicoWorkshop, AIPicoFreestyle, AIBooleanChecker) ───
    wkAiCheckBtn: "AI 檢查",
    wkAiCheckLoading: "分析中…",
    wkAiOverallBtn: "AI 整體評估我的 PICO",
    wkAiOverallLoading: "正在進行整體評估…",
    wkAiOverallTitle: "整體評估",
    wkResetShort: "重設",
    wkStartOver: "重新開始 ↻",
    wkSaveSync: "登入即可跨裝置同步",
    wkSaveSaving: "儲存中…",
    wkSaveSaved: "已自動儲存",
    wkSaveError: "儲存失敗",
    wkErrUnavailable: "AI 服務暫時無法使用，請稍後再試。",
    wkErrNoResult: "無法取得回饋，請重試。",
    wkErrConnection: "連線錯誤，請檢查網路後重試。",

    // ═══ COURSE 2: Literature Search & PRISMA ═══
    // Nav / Meta
    c2Label: "課程 2",
    c2Subtitle: "文獻搜尋與篩選",
    c2Title: "文獻搜尋與篩選 (PRISMA)",
    c2Desc: "系統性地搜尋文獻庫，學會布林邏輯搜尋策略，並用 PRISMA 流程圖記錄你的篩選過程。",
    c2StartBtn: "開始學習 →",

    // S1: Why Systematic Search
    c2s1Label: "為什麼要系統性搜尋？",
    c2s1Title: "隨意搜尋 vs. 系統性搜尋",
    c2s1Intro: "想像你在準備一場重要的法庭辯論，你不會只看 Google 第一頁的結果就上場。系統性文獻搜尋就像是偵探蒐證——你必須用結構化的方法搜遍所有相關資料庫，確保沒有遺漏關鍵證據。這不僅影響結論的可信度，更是同行評審的必要要求。",
    c2s1Bad: "❌ 隨意搜尋",
    c2s1BadEx: "「在 Google 打『statin heart』，看前幾篇」",
    c2s1BadWhy: "結果不可重複、可能遺漏重要文獻、無法說明你找了什麼、沒找到什麼。這不符合系統性回顧的標準。",
    c2s1Good: "✅ 系統性搜尋",
    c2s1GoodEx: "在 PubMed、Embase、Cochrane 用預先設計的搜尋策略，記錄每個資料庫的結果數，保存搜尋日期和語法。",
    c2s1GoodWhy: "可重現、全面、透明。其他研究者可以用同樣的策略得到同樣的結果。",
    c2s1Next: "認識常用資料庫 →",

    // S2: Databases
    c2s2Label: "文獻資料庫",
    c2s2Title: "你的搜尋從哪裡開始？",
    c2s2Intro: "系統性回顧通常需要搜尋至少 2-3 個資料庫。每個資料庫有不同的收錄範圍和搜尋語法，選對資料庫是成功的第一步。",
    c2dbPubmed: "最廣泛使用的生醫資料庫，收錄超過 3,600 萬篇文獻，支援 MeSH 詞彙搜尋。免費使用。",
    c2dbEmbase: "歐洲為主的生醫資料庫，藥理學和藥物安全性文獻特別豐富。收錄範圍與 PubMed 互補。",
    c2dbCochrane: "系統性回顧的金標準資料庫，包含 Cochrane Reviews 和 CENTRAL 臨床試驗註冊。",
    c2dbWos: "跨領域的引文索引資料庫，適合找工程、社會科學等交叉領域文獻。",
    c2dbCinahl: "護理學和健康照護相關文獻的專門資料庫，對護理主題特別全面。",
    c2dbGreyName: "灰色文獻",
    c2dbGrey: "未正式發表的文獻：會議摘要、學位論文、技術報告。用 ClinicalTrials.gov 和 ProQuest Dissertations 搜尋。",
    c2s2Tip: "Cochrane 建議至少搜尋 MEDLINE 和 Embase。如果你的主題涉及護理，加上 CINAHL；涉及心理學，加上 PsycINFO。",
    c2s2Next: "學習布林邏輯搜尋 →",

    // S3: Boolean Operators
    c2s3Label: "布林邏輯",
    c2s3Title: "AND、OR、NOT：搜尋的積木",
    c2s3Intro: "布林運算子是資料庫搜尋的基礎工具。它們決定了關鍵字之間的邏輯關係，直接影響你搜到的文獻數量和相關性。",
    c2boolTitle: "三個布林運算子",
    c2boolDesc: "點擊每個 Venn 圖來了解它的作用",
    c2boolClickHint: "👆 點擊上方的 Venn 圖查看說明",
    c2boolAndTitle: "AND — 交集（縮小範圍）",
    c2boolAndDesc: "兩個詞都必須出現。AND 會縮小搜尋結果，讓結果更精確。每多加一個 AND，結果就會變少。",
    c2boolAndExample: "heart failure AND SGLT2 inhibitor",
    c2boolOrTitle: "OR — 聯集（擴大範圍）",
    c2boolOrDesc: "任何一個詞出現即可。OR 用來連接同義詞或相關詞，確保不會遺漏用了不同術語的文獻。",
    c2boolOrExample: "diabetes OR hyperglycemia OR blood glucose",
    c2boolNotTitle: "NOT — 排除（移除不要的）",
    c2boolNotDesc: "排除包含某個詞的結果。小心使用——NOT 可能意外排除相關文獻。",
    c2boolNotExample: "cancer NOT animal NOT in vitro",
    c2s3ComboTitle: "實際搜尋策略：組合 AND + OR",
    c2s3ComboDesc: "核心原則：用 OR 連接每組概念內的同義詞（橫向擴大），用 AND 連接不同概念（縱向聚焦）。這就是建立搜尋策略的基本架構。",
    c2s3Next: "了解 PRISMA 篩選流程 →",

    // S4: PRISMA
    c2s4Label: "PRISMA 流程圖",
    c2s4Title: "篩選的路線圖",
    c2s4Intro: "PRISMA 2020 流程圖是系統性回顧的標準報告工具。它記錄了從搜尋到最終納入的每一步，讓讀者了解你的篩選過程和排除理由。",
    c2prismaTitle: "PRISMA 2020 流程圖",
    c2prismaDesc: "點擊「開始篩選」觀看動態流程，或點擊每個步驟查看說明",
    c2prismaIdNum: "2,847",
    c2prismaIdLabel: "識別：資料庫搜尋結果",
    c2prismaIdDesc: "從多個資料庫搜尋到的初始文獻數量。分別記錄每個資料庫的結果：如 PubMed 1,234 篇 + Embase 987 篇 + Cochrane 626 篇 = 2,847 篇。",
    c2prismaDupNum: "1,923",
    c2prismaDupLabel: "去重後：移除重複文獻",
    c2prismaDupDesc: "不同資料庫往往收錄相同的文獻，使用 EndNote、Covidence 或 Rayyan 等工具自動比對和移除重複。本例移除了 924 篇重複。",
    c2prismaDuplicatesExcl: "移除 924 篇重複",
    c2prismaScreenNum: "412",
    c2prismaScreenLabel: "初篩：標題和摘要篩選",
    c2prismaScreenDesc: "兩名獨立審稿者分別閱讀標題和摘要，根據預設的納入/排除標準判斷是否相關。不一致的由第三位裁決。使用 Rayyan 或 Covidence 等工具記錄決策。",
    c2prismaScreeningExcl: "排除 1,511 篇不相關",
    c2prismaEligNum: "48",
    c2prismaEligLabel: "全文篩選：詳細評估",
    c2prismaEligDesc: "取得全文後詳細評估。記錄每篇排除文獻的具體理由（如：非 RCT、族群不符、結果指標不匹配）。PRISMA 要求報告排除理由。",
    c2prismaEligibilityExcl: "排除 364 篇（附理由）",
    c2prismaInclNum: "18",
    c2prismaInclLabel: "納入：最終統合分析",
    c2prismaInclDesc: "通過全部篩選標準、符合 PICO 的研究被納入定量統合分析。可以進一步區分納入定性綜合和定量統合的研究數。",
    c2prismaRunBtn: "▶ 開始篩選動畫",
    c2prismaResetBtn: "↺ 重新播放",
    c2prismaRunning: "篩選中…",
    c2s4Next: "篩選技巧 →",

    // S5: Screening Tips
    c2s5Label: "篩選實用技巧",
    c2s5Title: "如何高效又準確地篩選？",
    c2s5Intro: "篩選上千篇文獻聽起來很嚇人，但掌握這些技巧可以大幅提升效率和準確度。",
    c2tip1Title: "預設納入/排除標準",
    c2tip1Desc: "在開始篩選前就明確訂出標準（基於你的 PICO），並寫成 protocol。這能減少主觀判斷，讓兩名審查者有一致的依據。",
    c2tip2Title: "兩人獨立篩選",
    c2tip2Desc: "至少兩名審查者獨立篩選，然後比對結果。計算 Cohen's kappa 評估一致性。意見不一致時由第三人裁定或透過討論取得共識。",
    c2tip3Title: "善用篩選工具",
    c2tip3Desc: "Rayyan、Covidence、ASReview 等工具支援盲篩、衝突標記和決策追蹤。比 Excel 高效得多，也更容易記錄過程。",
    c2tip4Title: "遇到不確定就先納入",
    c2tip4Desc: "初篩階段（標題/摘要）如果不確定，先標記為「可能納入」。寧可在全文篩選階段排除，也不要在初篩就誤刪。",
    c2s5Next: "挑戰小遊戲 →",

    // Game
    c2gameLabel: "搜尋策略小遊戲",
    c2gameTitle: "🔍 文獻搜尋大冒險",
    c2gameDesc: "測試你對文獻搜尋和篩選的理解！答對問題讓你的小恐龍助手開心。",
    c2gamePickDino: "選一隻小恐龍陪你挑戰！",
    c2gameMaster: "搜尋大師！🏆",
    c2gameGood: "做得不錯！👍",
    c2gameLearning: "繼續努力！📚",
    c2gameScore: (score, total) => `答對 ${score} / ${total} 題`,
    c2gamePlayAgain: "再玩一次",
    c2gameQ: (n, total) => `第 ${n} / ${total} 題`,
    c2gameNext: "下一題 →",
    c2gameResults: "查看結果 →",

    // AI Workshop
    c2aiLabel: "AI 搜尋策略工作坊",
    c2aiTitle: "讓 AI 幫你建立搜尋策略",
    c2aiDesc: "輸入你的研究主題，AI 會幫你拆解搜尋概念、建議關鍵字和 MeSH 詞，並生成可以直接在 PubMed 使用的搜尋語法。",
    c2aiInstructions: "貼上你的 PubMed 搜尋語法：",
    c2aiPlaceholder: "例如：SGLT2 抑制劑對心衰竭患者的死亡率影響",
    c2aiGenerate: "AI 生成搜尋策略",

    // S3b: MeSH & Controlled Vocabulary
    c2s3bLabel: "受控詞彙",
    c2s3bTitle: "MeSH、展開搜尋與截斷",
    c2s3bIntro: "光用自由文字搜尋是不夠的。資料庫使用受控詞彙（如 PubMed 的 MeSH）來標準化索引，確保同一概念的文獻能被找到。搜尋策略應同時使用 MeSH 詞和自由文字，以達到最佳涵蓋範圍。",
    c2s3Next2: "認識 MeSH 詞彙 →",

    // S6: Grey Literature
    c2s6Label: "灰色文獻",
    c2s6Title: "超越資料庫：灰色文獻搜尋",
    c2s6Intro: "只搜尋正式發表的文獻是不夠的。有統計上顯著結果的研究更容易被期刊接受（發表偏倚），所以搜尋未發表的灰色文獻可以減少偏倚，讓你的回顧更完整、更可靠。",
    c2s6Tip: "Cochrane Handbook 建議搜尋至少一個臨床試驗註冊平台（例如 ClinicalTrials.gov 或 WHO ICTRP），並且手動追蹤已納入文獻的參考文獻。",
    c2s6Next: "搜尋策略陷阱 →",

    // S7: Search Strategy Pitfalls
    c2s7Label: "注意事項",
    c2s7Title: "搜尋策略的常見陷阱",
    c2s7Intro: "即使使用了正確的布林邏輯和適當的資料庫，搜尋策略仍然可能存在問題。以下是最常見的陷阱和最佳實踐。",

    // Screening drill button
    c2s5Next2: "了解灰色文獻 →",

    // AI Workshop — Boolean Query Checker
    c2aiSectionTitle: "用 AI 審查你的搜尋語法",
    c2aiSectionDesc: "利用 AI 輔助練習搜尋策略。這些工具不能取代你的專業判斷，但可以幫助你更快地建立、審查和撰寫搜尋策略。",
    c2aiBDesc: "貼上你的 PubMed 搜尋語法，AI 會審查布林邏輯、遺漏的同義詞，並建議改善方案。",
    c2aiBPlaceholder: "例如：(heart failure) AND (SGLT2 inhibitor OR dapagliflozin) AND (mortality)",
    c2aiBBtn: "AI 審查我的搜尋語法",
    c2aiBLoading: "正在審查…",
    c2aiBResult: "AI 審查結果",
    c2aiError: "連線錯誤，請檢查網路後重試。",
    c2aiNoResult: "無法取得回饋",
    c2checkAnswers: "查看結果",
    c2tryAgain: "重試",
    c2correct: "正確",

    // ═══ COURSE 3: Data Extraction & Risk of Bias ═══
    c3Label: "課程 3",
    c3Title: "數據擷取與偏倚風險評估",
    c3Subtitle: "數據擷取與 RoB",
    c3Desc: "從納入的研究中精確地擷取數據，並評估每項研究的品質。就像烹飪前精確量測食材、檢查新鮮度一樣——這是確保統合分析結果可靠的關鍵步驟。",
    c3StartBtn: "開始學習 →",
    c3s1Label: "為什麼數據擷取重要？",
    c3s1Title: "讀營養標籤，不只看包裝",
    c3s1Intro: "想像你在超市買食物：包裝上寫著「健康」、「天然」，但真正重要的是營養標籤上的數字——熱量、蛋白質、鈉含量。數據擷取也一樣：不能只看摘要的結論，要仔細記錄原始數據。",
    c3s1Bad: "❌ 不好的做法",
    c3s1BadEx: "只從摘要抓取 p 值：「p = 0.03，有顯著差異」",
    c3s1BadWhy: "缺少了樣本量、事件數、效應量——就像只看「健康」標籤卻不看成分表。數據品質差，分析結果也不可靠。",
    c3s1Good: "✅ 好的做法",
    c3s1GoodEx: "結構化擷取：事件數 15/100 vs 22/100、均值±SD、研究設計、追蹤時間",
    c3s1GoodWhy: "原始數據讓統合分析軟體能準確計算效應量並正確加權每項研究。缺少這些數據，軟體就無法運算，或只能用不精確的替代值，直接影響結果的可信度。",
    c3s1Next: "來看看標準化擷取表格 →",
    c3s2Label: "擷取表格",
    c3s2Title: "你的標準化擷取表格",
    c3s2Intro: "就像食譜要求精確量測每種食材，數據擷取需要一張標準化表格，確保每篇研究都收集到相同的資訊。點擊下方每個欄位，看看該填什麼。",
    c3s2Tip: "烹飪類比：擷取表格就像你的量杯和量匙——確保每次「配方」都精確一致。在正式擷取前，先用 2-3 篇文獻試行，讓團隊統一標準。",
    c3s2Next: "接下來看該擷取什麼數字 →",
    c3s3Label: "擷取什麼數字？",
    c3s3Title: "二分類 vs 連續性結果",
    c3s3Intro: "結果數據分兩大類：二分類（是/否，如死亡率）和連續性（數值，如血壓變化）。每一類需要擷取不同的數字。點擊下方看詳情。",
    c3s3Next: "來了解為什麼品質評估很重要 →",
    c3s4Label: "為什麼品質重要？",
    c3s4Title: "偏倚風險：檢查食材的新鮮度",
    c3s4Intro: "不是所有納入的研究都可以信任。就像烹飪前要檢查食材是否新鮮——一個過期的食材就可能毀掉整道菜。一項高偏倚風險的研究可能扭曲整個統合分析的結果。",
    c3s4Analogy: "想像你做了一鍋湯，加了 5 種食材。其中 1 種已經壞了。即使其他 4 種都很新鮮，那一種壞食材就足以讓整鍋湯變味。敏感性分析就是：試著把那個可疑的食材拿掉，看看湯的味道會不會改變。",
    c3s4Next: "來認識品質評估工具 →",
    c3s5Label: "品質評估工具",
    c3s5Title: "兩大品質評估工具",
    c3s5Intro: "不同類型的研究使用不同的品質評估工具。隨機對照試驗用 Cochrane RoB 2（紅綠燈系統），觀察性研究用 Newcastle-Ottawa Scale（星星系統）。點擊下方卡片了解詳情。",
    c3s5Next: "把所有東西整合起來 →",
    c3s6Label: "整合",
    c3s6Title: "紅綠燈矩陣與敏感性分析",
    c3s6Intro: "把所有研究的偏倚風險評估放在一張紅綠燈表格裡，一目了然。然後用敏感性分析測試：排除高風險研究後，結論是否改變？",
    c3s6Tip: "關鍵原則：兩人獨立擷取數據和評估品質，記錄分歧及解決方式。透明度和可重複性是系統性回顧的基石。",
    c3s6Next: "繼續學習雙人數據擷取 →",
    c3gameLabel: "拯救恐龍的家 🏠",

    // AI Workshop — Extraction Reviewer
    c3aiLabel: "AI 數據擷取工作坊",
    c3aiTitle: "用 AI 審查你的數據擷取",
    c3aiError: "連線錯誤，請檢查網路後重試。",
    c3aiNoResult: "無法取得回饋",

    // ── Course 4: Effect Sizes & Forest Plots ──
    c4Label: "課程 4",
    c4Title: "效應量與森林圖",
    c4Subtitle: "效應量與森林圖",
    c4Desc: "一個研究說藥物「有效」——但效果有多大？學習如何量化效果大小、理解加權方法，以及閱讀和製作森林圖。",
    c4StartBtn: "開始學習 →",
    c4s1Label: "什麼是效應量？",
    c4s1Title: "不只是「有效」，而是「多有效」",
    c4s1Intro: "一個研究告訴你藥物「有效」——但這到底代表什麼？效應量就是那個關鍵數字：它量化了研究發現的大小和方向。不再只是「有差異」或「沒差異」的二元判斷，而是一個精確的分數。",
    c4s1Analogy: "把效應量想像成 Yelp 上餐廳的星級評分——不只是「推薦/不推薦」，而是具體幾分。你更信任一個 4.5 星的評價還是「還不錯」的模糊評語？效應量就是研究界的「星級評分」。",
    c4s1Next: "來看四種常見的效應量 →",
    c4s2Label: "效應量類型",
    c4s2Title: "四種關鍵效應量",
    c4s2Intro: "根據結果類型（二分類 vs 連續性）和量表是否相同，我們選擇不同的效應量。點擊下方卡片查看每一種的公式、用途和藥學範例。",
    c4s2Next: "接下來了解加權方法 →",
    c4s3Label: "加權",
    c4s3Title: "不是所有研究都平等",
    c4s3Intro: "想像你做一個民調：你會更信任 10,000 人的結果還是 10 人的？統合分析也一樣——更大、更精確的研究獲得更大的「投票權」。這就是反變異數加權的核心邏輯。",
    c4s3Next: "固定效應 vs 隨機效應 →",
    c4s4Label: "固定 vs 隨機效應",
    c4s4Title: "一個真實效應，還是很多個？",
    c4s4Intro: "固定效應模型假設所有研究都在估計同一個真實效應。隨機效應模型承認不同研究的效應可能真的不同（不同人群、劑量、設定）。切換下方的模型，觀察信賴區間如何變化。",
    c4s4Next: "來解剖一張森林圖 →",
    c4s5Label: "森林圖解剖",
    c4s5Title: "一片一片拆解森林圖",
    c4s5Intro: "森林圖是統合分析的「名片」——它在一張圖上展示了所有研究的效應量、信賴區間、權重和合併結果。將滑鼠停在下方森林圖的各個部分上，了解每個元素的含義。",
    c4s5Next: "來練習判讀森林圖 →",
    c4s6Label: "判讀森林圖",
    c4s6Title: "三題實戰練習",
    c4s6Intro: "現在你知道森林圖的每個元素了。來實際判讀三張練習森林圖，回答引導式問題：合併效應是否跨越無效線？哪個研究權重最大？整體結果顯著嗎？",
    c4s6Next: "準備好了嗎？來玩遊戲！🎮",
    c4gameLabel: "恐龍小遊戲 🎮",

    // ── Course 5: Heterogeneity & Publication Bias ──
    c5Label: "課程 5",
    c5Title: "異質性與發表偏倚",
    c5Subtitle: "異質性與發表偏倚",
    c5Desc: "你合併了 10 個研究，但它們的結果差異很大——能信任這個平均值嗎？學習評估研究間差異、檢測發表偏倚，以及正確報告你的統合分析。",
    c5StartBtn: "開始學習 →",
    c5s1Label: "什麼是異質性？",
    c5s1Title: "研究意見不一致——能信任平均值嗎？",
    c5s1Intro: "你做了一個統合分析，合併了 10 個研究。但仔細一看——有的研究說效果很大，有的說幾乎沒效果，有的甚至方向相反。這些「不一致」就是異質性。當異質性高時，合併效應可能就像平均北極和撒哈拉的溫度一樣沒有意義。",
    c5s1Analogy: "平均 5 個城市的溫度：如果一個在北極、一個在撒哈拉，平均溫度既不代表北極也不代表撒哈拉——它是一個毫無意義的數字。異質性高的統合分析也是如此。",
    c5s1Next: "來量化異質性 →",
    c5s2Label: "衡量異質性",
    c5s2Title: "I²、Q 和預測區間",
    c5s2Intro: "我們用三個工具來衡量異質性：Cochran's Q 檢定（有沒有超出預期的差異？）、I² 統計量（差異的百分比有多少來自真實異質性？）、和預測區間（未來一個新研究的效應會落在哪裡？）。拖動下方滑桿體驗不同程度的 I²。",
    c5s2Next: "為什麼會有異質性？ →",
    c5s3Label: "異質性來源",
    c5s3Title: "四種異質性的來源",
    c5s3Intro: "異質性不是隨機出現的——它有原因。我們需要找出是哪些因素導致了研究結果的不一致。是臨床設定不同？方法學不同？還是統計上的差異？當異質性高時，我們要探索為什麼，而不只是報告 I²。",
    c5s3Next: "如何探索異質性 →",
    c5s4Label: "探索異質性",
    c5s4Title: "次族群分析與統合迴歸",
    c5s4Intro: "找到異質性後，下一步是探索原因。兩種主要方法：次族群分析（把研究按某個因素分組比較）和統合迴歸（看連續性因素和效應的趨勢）。下方示範展示了按劑量分組如何「解開」異質性之謎。",
    c5s4Next: "來認識發表偏倚 →",
    c5s5Label: "發表偏倚",
    c5s5Title: "抽屜裡的「消失」研究",
    c5s5Intro: "如果結果不顯著的研究被丟在抽屜裡不發表呢？你的統合分析就只看到了「成功」的研究，系統性地高估了效果。這就是發表偏倚——漏斗圖可以幫助你檢測它。",
    c5s5Tip: "除了漏斗圖，還有 Egger's test（統計檢定漏斗圖不對稱）和修剪填補法（trim-and-fill，估計缺失研究並重新計算合併效應）。但所有方法都有局限性，需要至少 10 個研究才有意義。",
    c5s5Next: "最後一步：正確報告 →",
    c5s6Label: "完整報告",
    c5s6Title: "PRISMA 2020 與完整報告",
    c5s6Intro: "整個課程的旅程到了最後一站。一個好的統合分析不只是計算合併效應——還需要透明、完整地報告整個過程。PRISMA 2020 清單是你的「報告指南針」。",
    c5s6Tip: "恭喜你完成了整個統合分析的學習旅程！從 PICO 到搜尋、篩選、擷取、品質評估、效應量合併、異質性探索、偏倚檢測到報告——這就是一個完整的統合分析。記住：統合分析是強大的工具，但它的品質取決於方法學的嚴謹性和你的批判性思維。",
    c5s6Next: "準備好了嗎？來玩最後一個遊戲！🎮",
    c5gameLabel: "最終恐龍遊戲 🎮",
  },

  en: {
    // Nav
    navTitle: "Meta-Analysis",
    navTitleSuffix: "101",
    navWhat: "What",
    navWhy: "Why",
    navDemo: "Demo",
    navHow: "How",
    navPlots: "Plots",
    navQuiz: "Egg Hunt",
    langSwitch: "中文",

    // Hero
    heroBadge: "A Beginner's Guide",
    heroTitle1: "What on earth is a ",
    heroTitle2: "Meta-Analysis",
    heroTitle3: "?",
    heroDesc: "Imagine reading every study on a topic, then mathematically combining them to find the real answer. That's meta-analysis — and it's simpler than you think.",
    heroBtn: "Start Learning →",

    // What section
    whatLabel: "The Basics",
    whatTitle: "So, What Is a Meta-Analysis?",
    whatIntro: "Picture this: you want to know if a new medication works. You find 20 studies — some say yes, some say no, some say maybe. Instead of just picking the one you like best, a meta-analysis mathematically combines all 20 results into a single, more reliable answer. It's like asking 20 experts and calculating the group consensus, giving more weight to the experts who are most confident.",
    whatCard1Title: "It's Math, Not Opinion",
    whatCard1Text: "Unlike a regular literature review where an author summarizes in their own words, meta-analysis uses statistical formulas to calculate a precise combined result.",
    whatCard2Title: "Not All Studies Are Equal",
    whatCard2Text: "Larger, more precise studies get more influence. A study with 1,000 patients counts more than one with 20 — just as you'd trust a larger poll more.",
    whatCard3Title: "Part of a Bigger Process",
    whatCard3Text: "Meta-analysis lives inside a 'systematic review' — a structured process for finding, evaluating, and combining all relevant studies on a question.",
    whatAnalogyTitle: "The Restaurant Review Analogy",
    whatAnalogyText: "Would you pick a restaurant based on one review? Probably not — you'd read many reviews and form an overall impression. But you'd weigh a review from someone who's visited 5 times over someone who just walked past. That's exactly what meta-analysis does with scientific studies: reads them all, weighs them by quality and size, and gives you the verdict.",

    // Why section
    whyLabel: "The Purpose",
    whyTitle: "Why Bother Doing One?",
    whyIntro: "Single studies are like puzzle pieces — valuable on their own, but you need to see them together to understand the full picture.",
    whyCard1Title: "More Statistical Power",
    whyCard1Text: "Small studies often can't detect real effects. By pooling data, meta-analysis achieves the power of a much larger study — sometimes tens of thousands of participants.",
    whyCard2Title: "Settle Disagreements",
    whyCard2Text: "When Study A says 'yes' and Study B says 'no,' meta-analysis provides a fair, mathematical arbitration instead of cherry-picking.",
    whyCard3Title: "Find Hidden Patterns",
    whyCard3Text: "Through subgroup analysis, you can discover a treatment works for older patients but not younger ones — something no single study might reveal.",
    whyCard4Title: "Shape Real Decisions",
    whyCard4Text: "Meta-analyses sit at the top of the evidence pyramid. Governments, hospitals, and guideline panels rely on them for treatment and policy decisions.",
    whyCard5Title: "Expose Missing Evidence",
    whyCard5Text: "Tools like funnel plots can reveal if studies with negative results weren't published — a phenomenon called publication bias.",
    whyCard6Title: "Map Future Research",
    whyCard6Text: "By revealing what we know and don't know, meta-analyses highlight gaps and guide where new studies are most needed.",

    // Combiner
    combinerLabel: "See It In Action",
    combinerTitle: "Watch Studies Combine",
    combinerDesc: 'This interactive demo shows how meta-analysis works. Eight studies each found different results — click "Combine" to see how weighting by sample size produces a single, more trustworthy answer.',
    combinerScattered: "Eight Studies, Eight Different Answers",
    combinerCombining: "Combining the evidence...",
    combinerCombined: "One Pooled Estimate",
    combinerScatteredDesc: "Each dot is a study. They disagree — which one do you trust?",
    combinerCombiningDesc: "Weighting by sample size to find the true signal...",
    combinerCombinedDesc: (pooled) => `Pooled effect: ${pooled} — larger studies pulled the estimate toward a small positive effect`,
    combinerNegative: "← Negative effect",
    combinerPositive: "Positive effect →",
    combinerNoEffect: "No Effect",
    combinerCombineBtn: "Combine Studies →",
    combinerResetBtn: "↺ Reset",

    // Study descriptions
    studyADesc: "Small RCT — found a moderate positive effect",
    studyBDesc: "Large trial — found almost no effect",
    studyCDesc: "Small study — found a strong effect",
    studyDDesc: "Largest trial — found a small positive effect",
    studyEDesc: "Medium study — found a moderate effect",
    studyFDesc: "Medium trial — found a negative effect",
    studyGDesc: "Large trial — found nearly no effect",
    studyHDesc: "Medium study — found a moderate positive effect",

    // How section
    howLabel: "Step by Step",
    howTitle: "How To Conduct a Meta-Analysis",
    howDesc: "It's a structured 8-step process. Click each step to expand the details and see a real-world analogy that makes it click.",
    howNote: "Based on PRISMA 2020 guidelines and established methodological frameworks",
    howThinkOfIt: "💡 Think of it like: ",

    step1Title: "Define Your Research Question",
    step1Analogy: "Deciding exactly what recipe you want to cook before going grocery shopping.",
    step1Detail1: "Use a structured framework like PICO: Population, Intervention, Comparison, Outcome.",
    step1Detail2: "Your question determines everything — which studies to include, what data to extract, and how to analyze it.",
    step1Detail3: "Make sure enough studies exist on your topic to justify a quantitative synthesis.",
    step1Detail4: "Write a detailed protocol and register it (e.g., on PROSPERO) before you begin.",

    step2Title: "Search the Literature Systematically",
    step2Analogy: "Casting a wide fishing net across multiple oceans to make sure you don't miss any fish.",
    step2Detail1: "Search multiple databases: PubMed, Embase, Cochrane Library, and others relevant to your field.",
    step2Detail2: "Use carefully constructed keyword combinations with Boolean operators (AND, OR, NOT).",
    step2Detail3: "Don't forget the 'gray literature' — dissertations, conference abstracts, preprints — to reduce bias.",
    step2Detail4: "Keep meticulous records of every search. You'll need to report this in a PRISMA flow diagram.",

    step3Title: "Screen & Select Studies",
    step3Analogy: "Sorting through your catch to keep only the fish that match what you're looking for.",
    step3Detail1: "Apply your pre-defined eligibility criteria to every study found.",
    step3Detail2: "At least two reviewers should independently screen titles, abstracts, then full texts.",
    step3Detail3: "Use reference management tools (EndNote, Mendeley) and screening software (Rayyan, Covidence).",
    step3Detail4: "Document exactly why each excluded study was removed — transparency is key.",

    step4Title: "Assess Quality & Risk of Bias",
    step4Analogy: "Checking the freshness and quality of each ingredient before cooking.",
    step4Detail1: "Use validated tools: Cochrane Risk of Bias for RCTs, Newcastle-Ottawa Scale for observational studies.",
    step4Detail2: "Evaluate randomization, blinding, incomplete data, and selective reporting.",
    step4Detail3: "Low-quality studies can distort your overall result — you may need sensitivity analyses excluding them.",
    step4Detail4: "Two independent reviewers should assess quality, with a process to resolve disagreements.",

    step5Title: "Extract Data & Calculate Effect Sizes",
    step5Analogy: "Measuring each ingredient precisely before adding it to the pot.",
    step5Detail1: "Use standardized forms to capture sample sizes, means, standard deviations, and event counts.",
    step5Detail2: "Choose your effect size metric: Odds Ratio for yes/no outcomes, Mean Difference for continuous measures.",
    step5Detail3: "Each study gets encoded as a single number (effect size) plus a measure of precision.",
    step5Detail4: "Double-check extractions — even published meta-analyses have been found to contain errors.",

    step6Title: "Pool Results & Analyze",
    step6Analogy: "Combining all your carefully measured ingredients and seeing what dish emerges.",
    step6Detail1: "Choose: Fixed-effect (assumes one true effect) vs. Random-effects (assumes effects vary between studies).",
    step6Detail2: "Each study is weighted by precision — larger, more precise studies have more influence.",
    step6Detail3: "Assess heterogeneity with I²: below 25% is low, 25–50% moderate, above 50% high.",
    step6Detail4: "If heterogeneity is high, explore why with subgroup analyses or meta-regression.",

    step7Title: "Check for Publication Bias",
    step7Analogy: "Making sure your recipe book isn't missing pages — especially the ones with failed recipes.",
    step7Detail1: "Create a funnel plot: study effects vs. precision. Symmetry suggests no bias.",
    step7Detail2: "Use Egger's test or Begg's test to statistically quantify potential bias.",
    step7Detail3: "The trim-and-fill method can estimate how many studies might be 'missing' and adjust the result.",
    step7Detail4: "Remember: funnel plot asymmetry can also reflect genuine heterogeneity, not just bias.",

    step8Title: "Report Your Findings",
    step8Analogy: "Writing up the recipe so anyone else could recreate your dish exactly.",
    step8Detail1: "Follow the PRISMA 2020 guidelines — a 27-item checklist covering every aspect of your review.",
    step8Detail2: "Include a PRISMA flow diagram showing how many studies were found, screened, and included.",
    step8Detail3: "Present results in forest plots with all key statistics: effect sizes, confidence intervals, I², p-values.",
    step8Detail4: "Discuss limitations openly: quality of included studies, potential biases, and generalizability.",

    // Forest plot
    forestLabel: "Key Visualizations",
    forestTitle: "Reading a Forest Plot",
    forestDesc: "The forest plot is the signature visualization of meta-analysis. Every element has a meaning — click on different parts below to learn what they represent.",
    forestNote: "In this example, studies are testing whether a treatment reduces a negative outcome. Values to the left mean the treatment helps.",
    forestClickHint: "👆 Click on a square, line, the center line, or the diamond below",
    forestFavorsTreatment: "← Favors Treatment",
    forestFavorsControl: "Favors Control →",
    forestOverall: "Overall",
    forestSquareTitle: "Study Effect (Square)",
    forestSquareText: "Each square shows one study's result. Its size reflects the study's weight — bigger squares mean larger, more precise studies that contribute more to the final answer.",
    forestLineTitle: "Confidence Interval (Line)",
    forestLineText: "The horizontal line shows the range of plausible values. Shorter lines = more precision. If it crosses the center line, the result isn't statistically significant on its own.",
    forestCenterTitle: "Line of No Effect",
    forestCenterText: "This vertical line represents zero effect — no difference between groups. Studies to the left favor treatment; studies to the right favor control.",
    forestDiamondTitle: "Pooled Estimate (Diamond)",
    forestDiamondText: "The diamond is the combined result of ALL studies. Its width shows the confidence interval. If the entire diamond sits on one side of the center line, the overall effect is statistically significant.",

    // Glossary
    glossaryEffectSize: "Effect Size",
    glossaryEffectSizeDef: "A number measuring how big the difference is between two groups. Common types: odds ratios, risk ratios, and standardized mean differences.",
    glossaryCI: "Confidence Interval",
    glossaryCIDef: "The range where the true effect likely falls. A 95% CI means we're 95% confident the real answer is somewhere in that range.",
    glossaryI2: "I² (Heterogeneity)",
    glossaryI2Def: "What percentage of variation between studies is real (not random noise). Under 25% = low, over 75% = very high.",
    glossaryFunnel: "Funnel Plot",
    glossaryFunnelDef: "A scatter plot checking for publication bias. Studies should scatter symmetrically — if not, some results may be missing.",
    glossaryFixedRandom: "Fixed vs. Random Effects",
    glossaryFixedRandomDef: "Fixed assumes one true effect exists. Random assumes effects vary between studies. Random is more common and conservative.",
    glossaryPRISMA: "PRISMA",
    glossaryPRISMADef: "The 27-item reporting checklist ensuring your meta-analysis is transparent, reproducible, and complete. Updated in 2020.",

    // Egg Hunt UI
    eggHuntLabel: "Test Yourself",
    eggHuntTitle: "Egg Hunt",
    eggHuntDesc: "Find 7 hidden eggs and test your meta-analysis knowledge! Each correct answer collects an egg in your basket.",
    eggHuntStart: "Start Hunt →",
    eggHuntProgress: (current) => `Egg ${current} of 7`,
    eggHuntCorrect: (name) => `You found the ${name}!`,
    eggHuntWrong: "The egg cracked...",
    eggHuntNext: "Next Egg →",
    eggHuntResults: "See Your Basket →",
    eggHuntBasketTitle: "Your Egg Basket",
    eggHuntScore: (n) => `You found ${n} out of 7 eggs!`,
    eggHuntMaster: "Meta-Analysis Master",
    eggHuntExplorer: "Meta-Analysis Explorer",
    eggHuntApprentice: "Meta-Analysis Apprentice",
    eggHuntKeepTrying: "Keep exploring!",
    eggHuntDownload: "Download Cheat Sheet",
    eggHuntPlayAgain: "Hunt Again →",
    eggHuntAllSheets: "Download Combined Cheat Sheet",

    // Egg category names
    eggCatDiscovery: "Discovery Egg",
    eggCatData: "Data Egg",
    eggCatForest: "Forest Egg",
    eggCatVariety: "Variety Egg",
    eggCatSearch: "Search Egg",
    eggCatBias: "Bias Egg",
    eggCatWisdom: "Wisdom Egg",

    // Cheat sheet names
    eggSheetDiscovery: "What Is a Meta-Analysis?",
    eggSheetData: "Data Extraction Pocket Guide",
    eggSheetForest: "How to Read a Forest Plot",
    eggSheetVariety: "Heterogeneity Cheat Sheet",
    eggSheetSearch: "Search & Screening Guide",
    eggSheetBias: "Bias Detective Card",
    eggSheetWisdom: "Interpretation Compass",

    // Footer
    footerText: "Content synthesized from leading methodological guides including PRISMA 2020, Cochrane Handbook, and peer-reviewed meta-analysis tutorials. Built as an educational resource for beginners.",
    footerBrand: "Meta-Analysis 101",

    // ═══ COURSE HUB ═══
    hubNavTitle: "Meta-Analysis",
    hubNavSuffix: "101",
    navCourses: "Courses",
    navAbout: "About",
    navLogin: "Sign In",
    navLogout: "Logout",
    navLoginSave: "Sign in to save progress",
    navProfile: "My Progress",

    // About page
    aboutHeroTitle: "About Meta-Analysis 101",
    aboutHeroDesc: "An interactive learning platform designed for pharmacists and clinical researchers to learn meta-analysis from scratch.",
    aboutGoalTitle: "Our Goal",
    aboutGoalText: "Make meta-analysis approachable. Through interactive charts and fun mini-games, we break down complex methodological concepts into bite-sized lessons anyone can follow.",
    aboutAudienceTitle: "Who Is This For?",
    aboutAudienceText: "Primarily pharmacists with no research background — but any healthcare professional interested in evidence-based medicine is welcome. No statistics prerequisite required; we start from the very basics.",
    aboutStructureTitle: "Course Structure",
    aboutStructureText: "Six progressive courses split into two parts. Foundations covers planning and preparation (concepts, PICO, literature search, data extraction & risk of bias). Advanced courses cover analysis and interpretation (effect sizes, forest plots, heterogeneity, publication bias). Between each part is an AI-assisted research workshop where you practice the full workflow. Every course ends with a dinosaur-themed mini-game!",
    aboutSourcesTitle: "References & Sources",
    aboutSourcesText: "Content synthesized from PRISMA 2020, the Cochrane Handbook, and peer-reviewed meta-analysis tutorials. All teaching materials have been carefully reviewed for accuracy.",
    aboutBuiltWith: "Built With",
    aboutBackToHub: "Back to Course Hub",

    // Profile page
    profileLoginRequired: "Sign In Required",
    profileLoginDesc: "Sign in with your Google account to track your learning progress, collected dinosaurs, and game scores.",
    profileOverview: "Progress Overview",
    profileEggs: "Eggs Collected",
    profileHatched: "Dinos Hatched",
    profileFed: "Dinos Fed",
    profileSaved: "Homes Saved",
    profileKeys: "Keys Earned",
    profileEscaped: "Escaped",
    profileDinoCollection: "Dino Collection",
    profileBestScores: "Best Scores",
    profileGame0: "Egg Hunt",
    profileGame1: "Egg Hatch",
    profileGame2: "Food Rescue",
    profileGame3: "Home Save",
    profileGame4: "Key Quest",
    profileGame5: "Map Escape",
    profileEmpty: "No game records yet. Start learning to collect your dinosaurs!",
    profileStartLearning: "Start Learning →",
    hubBadge: "LEARNING PLATFORM",
    hubTitle1: "Master ",
    hubTitle2: "Meta-Analysis",
    hubDesc: "From foundational concepts to hands-on analysis — 6 progressive courses that take you through the complete meta-analysis workflow. Each course ends with a mini-game!",
    hubPrecourse: "Precourse",
    hubPrecourseDesc: "Introduction to Meta-Analysis",
    hubSection1: "Foundations",
    hubSection1Desc: "Courses 1-3 · PICO, Literature search, Data extraction",
    hubSection2: "Advanced Courses",
    hubSection2Desc: "Courses 4-5 · Effect sizes, Forest plots, Heterogeneity, Pub bias",
    hubMidtermTitle: "MA Workshop: Planning",
    hubMidtermDesc: "Define your question, search literature, extract data, assess risk of bias",
    hubFinalTitle: "MA Workshop: Analysis",
    hubFinalDesc: "Run your meta-analysis, interpret results, write conclusions",
    hubC0Title: "What Is Meta-Analysis?",
    hubC0Desc: "Learn the basics: what it is, why it matters, and the step-by-step process.",
    hubC0Game: "Egg Hunt Game",
    hubC1Title: "Crafting Your Research Question (PICO)",
    hubC1Desc: "Turn vague ideas into precise, searchable questions using the PICO/PICOS framework.",
    hubC1Game: "Dragon Egg Hatching Game",
    hubC2Title: "Literature Search & Screening (PRISMA)",
    hubC2Desc: "Systematically search databases and document your screening with PRISMA flow diagrams.",
    hubC2Game: "Dino Food Rescue",
    hubC3Title: "Data Extraction & Risk of Bias",
    hubC3Desc: "Extract data from included studies and assess each study's quality.",
    hubC3Game: "Save Dino's Home",
    hubC4Title: "Effect Sizes & Forest Plots",
    hubC4Desc: "Calculate effect sizes, understand weighting, and learn to read and create forest plots.",
    hubC4Game: "Dino Key Quest",
    hubC5Title: "Heterogeneity & Publication Bias",
    hubC5Desc: "Assess between-study variation, detect publication bias, and master PRISMA reporting essentials.",
    hubC5Game: "Dino Door Escape",
    hubComingSoon: "Coming Soon",
    hubFooter: "Meta-Analysis 101 — A complete learning path from basics to advanced",

    // ═══ COURSE 1: PICO ═══
    c1Label: "Course 1",
    c1Title: "Crafting Your Research Question",
    c1Subtitle: "PICO / PICOS Framework",
    c1Desc: "A great meta-analysis starts with a great question. Learn to turn vague ideas into precise, searchable, answerable research questions using the PICO framework.",
    c1StartBtn: "Start Learning →",

    c1s1Label: "Why PICO?", c1s1Title: "Vague Questions Get Vague Answers",
    c1s1Intro: "Imagine walking into a library and asking: \"Got any books about hearts?\" The librarian would be lost. But if you ask: \"Do you have RCTs comparing aspirin vs placebo for preventing MI?\" — now that's searchable.",
    c1s1Bad: "❌ Bad Question", c1s1BadEx: "\"Do diabetes drugs work?\"", c1s1BadWhy: "Which drug? For whom? Compared to what? Measuring what? Too vague.",
    c1s1Good: "✅ Good PICO Question", c1s1GoodEx: "\"In adults with type 2 diabetes (P), does Metformin (I) compared to placebo (C) reduce HbA1c (O)?\"", c1s1GoodWhy: "Every element is clearly defined. You can go straight to PubMed with this.",
    c1s1Next: "Let's learn the four PICO elements →",

    c1s2Label: "The Four Elements", c1s2Title: "Breaking Down PICO",
    c1s2Intro: "PICO is an acronym where each letter locks down one dimension of your research question:",
    c1pTitle: "P — Population", c1pDesc: "Who are you studying? The more specific, the better.", c1pExample: "\"Adults over 65 with a history of hypertension\"", c1pTip: "Think: age, sex, disease stage, severity, setting",
    c1iTitle: "I — Intervention", c1iDesc: "What treatment, drug, strategy, or exposure are you testing?", c1iExample: "\"Amlodipine 5mg daily, oral\"", c1iTip: "Think: dose, frequency, route, duration",
    c1cTitle: "C — Comparison", c1cDesc: "What are you comparing against? Placebo, another drug, or no treatment.", c1cExample: "\"Placebo\" or \"Lisinopril 10mg\"", c1cTip: "No comparison = no causal inference. This is the heart of evidence.",
    c1oTitle: "O — Outcome", c1oDesc: "What do you measure to judge success or failure?", c1oExample: "\"Change in systolic BP (mmHg)\" or \"CV event rate\"", c1oTip: "Clarify both primary and secondary outcomes",
    c1s2Next: "Now let's extend to PICOS →",

    c1s3Label: "Advanced: PICOS", c1s3Title: "Adding S — Study Design",
    c1s3Intro: "Sometimes you need a fifth letter: S (Study design). It specifies which types of studies you'll include.",
    c1sTitle: "S — Study Design", c1sDesc: "What study types will you include? This affects your search scope and evidence quality.", c1sOptions: "Common choices:",
    c1sRCT: "Randomized Controlled Trials (RCTs) — highest evidence level", c1sCohort: "Cohort studies — observational, good for long-term follow-up", c1sCaseControl: "Case-control studies — good for rare diseases", c1sAny: "All study designs — when RCTs are scarce",
    c1s3Tip: "If your meta-analysis only includes RCTs, state it clearly in S. This directly affects your screening criteria in Course 2.",
    c1s3Next: "Let's check common mistakes first →",

    c1s4Label: "Interactive Exercise", c1s4Title: "Pick the Best PICO",
    c1s4Intro: "Choose a clinical scenario, then pick the best definition for each P, I, C, O element. You'll get instant feedback at every step.",
    c1s4Scenario: "Pick a scenario to start:",
    c1scenarioA: "🫀 Cardiology: SGLT2 inhibitors for heart failure", c1scenarioB: "🦠 Infectious Disease: Prolonged β-lactam infusion", c1scenarioC: "🧠 Psychiatry: CBT for major depression",
    c1s4YourP: "Your P (Population):", c1s4YourI: "Your I (Intervention):", c1s4YourC: "Your C (Comparison):", c1s4YourO: "Your O (Outcome):",
    c1s4PlaceholderP: "e.g., Adults over 65 with heart failure...", c1s4PlaceholderI: "e.g., Dapagliflozin 10mg QD...", c1s4PlaceholderC: "e.g., Placebo...", c1s4PlaceholderO: "e.g., Hospitalization rate...",
    c1s4ShowAnswer: "Show Reference Answer", c1s4HideAnswer: "Hide Reference Answer", c1s4RefTitle: "Reference PICO",
    c1scenarioARef: { p: "Adults with HFrEF, NYHA class II-IV", i: "SGLT2 inhibitor (Dapagliflozin 10mg or Empagliflozin 10mg) plus standard therapy", c: "Placebo plus standard therapy", o: "Primary: composite of CV death or HF hospitalization; Secondary: all-cause mortality, KCCQ" },
    c1scenarioBRef: { p: "Critically ill adults in ICU receiving β-lactam antibiotics", i: "Prolonged infusion (≥3h continuous or extended intermittent)", c: "Conventional intermittent infusion (30-min bolus)", o: "Primary: in-hospital mortality; Secondary: clinical cure rate, ICU LOS" },
    c1scenarioCRef: { p: "Adults (≥18y) meeting DSM-5 criteria for major depressive disorder", i: "CBT + standard pharmacotherapy (SSRI), weekly sessions for 12-16 weeks", c: "Standard pharmacotherapy (SSRI) alone", o: "Primary: depression score change (PHQ-9/HAM-D); Secondary: remission rate, relapse rate" },
    c1s4Next: "Now let's practice! →",

    c1s5Label: "Common Mistakes", c1s5Title: "The 5 PICO Traps",
    c1s5Intro: "The most common beginner mistakes — avoid these and you're halfway to a great research question.",
    c1trap1Title: "Population Too Broad", c1trap1Bad: "\"All diabetes patients\"", c1trap1Good: "\"Adults with T2DM, HbA1c > 7%, insulin-naive\"",
    c1trap2Title: "No Clear Comparator", c1trap2Bad: "\"Does Metformin work?\"", c1trap2Good: "\"Metformin vs placebo\" or \"Metformin vs Sulfonylurea\"",
    c1trap3Title: "Vague Outcome", c1trap3Bad: "\"Did patients get better?\"", c1trap3Good: "\"Mean change in HbA1c at 12 weeks\"",
    c1trap4Title: "Question Too Broad", c1trap4Bad: "\"All antihypertensives for all CV diseases\"", c1trap4Good: "\"ACEi vs ARBs on stroke risk in hypertensive patients\"",
    c1trap5Title: "Forgetting Feasibility", c1trap5Bad: "Perfect PICO but only 2 relevant studies exist", c1trap5Good: "Do a preliminary search to confirm enough studies before finalizing",
    c1s5Next: "Head to the Dragon Egg Challenge! 🥚🔥",

    c1gameLabel: "Dragon Egg Challenge", c1gameTitle: "Can You Hatch the Dinosaurs?",
    c1gameDesc: "Pick a dragon egg, then answer 7 PICO questions. Each correct answer brings sunshine to crack the egg! But each wrong answer brings frost — 3 wrong and the egg freezes solid…",
    c1gameStart: "Start Hatching 🥚",
    c1gameQ: (n) => `Question ${n} of 7`,
    c1gameCorrect: "Correct! The egg is cracking! 🔥", c1gameWrong: "Wrong… the egg feels a chill ❄️",
    c1gameNext: "Next →", c1gameResults: "See Results →",
    c1gameHatched: "The Eggs Hatched! 🎉", c1gameHatchedDesc: "Your baby dinosaurs are born! Each one represents your mastery of PICO.",
    c1gameFrozen: "The Eggs Froze… ❄️", c1gameFrozenDesc: "Don't worry! Review the PICO content and try again!",
    c1gameScore: (s) => `☀️ Collected ${s} sunshine`,
    c1gameRetry: "Try Again 🔄", c1gameContinue: "Continue to AI Workshop →",

    c1dinoNames: ["Rex", "Azure", "Zephyr", "Blaze", "Thistle", "Velo", "Dome"],

    // AI PICO Workshop
    c1aiLabel: "AI Workshop",
    c1aiTitle: "Let AI Review Your PICO",
    c1aiDesc: "Pick a clinical scenario, write your own PICO, then let AI check each element and give you personalized feedback. Fill in all four fields for an overall assessment!",
    c1aiScenario: "Choose a scenario:",

    // AI Freestyle PICO
    c1freeLabel: "Freestyle",
    c1freeTitle: "Practice PICO with Your Own Topic",
    c1freeDesc: "Have a research question in mind? Write your topic and let AI check if it's suitable, then evaluate your PICO element by element. This is your first step from classroom exercises to real research!",
    c1freeTopic: "Your research topic:",
    c1freeTopicHint: "Describe your clinical question in 1-2 sentences, e.g. 'I want to study whether probiotics can prevent C. diff infection in ICU patients'",
    c1freeTopicPlaceholder: "e.g. Effect of SGLT2 inhibitors on CKD progression...",
    c1freeTopicCheck: "AI Check Topic",
    c1freeNowPico: "Now write your PICO",
    c1freePlaceholderP: "Define the study population for your topic...",
    c1freePlaceholderI: "The intervention you want to test...",
    c1freePlaceholderC: "What's the comparator...",
    c1freePlaceholderO: "The outcome you want to measure...",

    // ─── Shared workshop UI strings (used by AIPicoWorkshop, AIPicoFreestyle, AIBooleanChecker) ───
    wkAiCheckBtn: "AI Check",
    wkAiCheckLoading: "Checking…",
    wkAiOverallBtn: "AI Overall Assessment",
    wkAiOverallLoading: "Running overall assessment…",
    wkAiOverallTitle: "Overall Assessment",
    wkResetShort: "Reset",
    wkStartOver: "Start Over ↻",
    wkSaveSync: "Sign in to sync progress",
    wkSaveSaving: "Saving…",
    wkSaveSaved: "Autosaved",
    wkSaveError: "Save failed",
    wkErrUnavailable: "AI service temporarily unavailable. Please try again later.",
    wkErrNoResult: "Could not get feedback. Please try again.",
    wkErrConnection: "Connection error. Please check your network and try again.",

    // ═══ COURSE 2: Literature Search & PRISMA ═══
    // Nav / Meta
    c2Label: "Course 2",
    c2Subtitle: "Literature Search & Screening",
    c2Title: "Literature Search & Screening (PRISMA)",
    c2Desc: "Systematically search databases, master Boolean logic, and document your screening process with PRISMA flow diagrams.",
    c2StartBtn: "Start Learning →",

    // S1
    c2s1Label: "Why Systematic Search?",
    c2s1Title: "Casual Search vs. Systematic Search",
    c2s1Intro: "Imagine preparing for an important court case — you wouldn't just glance at the first page of Google results. A systematic search is like detective work: you must methodically search all relevant databases using a structured strategy to ensure no key evidence is missed. This affects the credibility of your conclusions and is a requirement for peer review.",
    c2s1Bad: "❌ Casual Search",
    c2s1BadEx: "\"Google 'statin heart', read first few articles\"",
    c2s1BadWhy: "Results are not reproducible, may miss important studies, and you can't document what you searched or what you missed. This doesn't meet systematic review standards.",
    c2s1Good: "✅ Systematic Search",
    c2s1GoodEx: "Search PubMed, Embase, and Cochrane using a pre-designed strategy, recording the number of results from each database, saving search dates and syntax.",
    c2s1GoodWhy: "Reproducible, comprehensive, transparent. Other researchers can use the same strategy and get the same results.",
    c2s1Next: "Explore Key Databases →",

    // S2
    c2s2Label: "Databases",
    c2s2Title: "Where Does Your Search Begin?",
    c2s2Intro: "A systematic review typically requires searching at least 2-3 databases. Each database has different coverage and search syntax — choosing the right ones is the first step to success.",
    c2dbPubmed: "The most widely used biomedical database with 36M+ records. Supports MeSH vocabulary. Free to use.",
    c2dbEmbase: "European-focused biomedical database, especially rich in pharmacology and drug safety literature. Complements PubMed coverage.",
    c2dbCochrane: "The gold standard for systematic reviews. Includes Cochrane Reviews and the CENTRAL clinical trials registry.",
    c2dbWos: "Cross-disciplinary citation index. Good for engineering, social sciences, and interdisciplinary topics.",
    c2dbCinahl: "Specialized database for nursing and allied health literature. Comprehensive for nursing topics.",
    c2dbGreyName: "Grey Literature",
    c2dbGrey: "Unpublished research: conference abstracts, dissertations, technical reports. Search via ClinicalTrials.gov and ProQuest.",
    c2s2Tip: "Cochrane recommends searching at least MEDLINE and Embase. Add CINAHL for nursing topics; PsycINFO for psychology.",
    c2s2Next: "Learn Boolean Logic →",

    // S3
    c2s3Label: "Boolean Logic",
    c2s3Title: "AND, OR, NOT: The Building Blocks of Search",
    c2s3Intro: "Boolean operators are the fundamental tools for database searching. They determine the logical relationship between keywords, directly affecting the number and relevance of your results.",
    c2boolTitle: "Three Boolean Operators",
    c2boolDesc: "Click each Venn diagram to learn how it works",
    c2boolClickHint: "👆 Click a Venn diagram above to see the explanation",
    c2boolAndTitle: "AND — Intersection (Narrow Results)",
    c2boolAndDesc: "Both terms must appear. AND narrows your search results, making them more precise. Each additional AND reduces results further.",
    c2boolAndExample: "heart failure AND SGLT2 inhibitor",
    c2boolOrTitle: "OR — Union (Expand Results)",
    c2boolOrDesc: "Either term can appear. OR connects synonyms and related terms, ensuring you don't miss studies that used different terminology.",
    c2boolOrExample: "diabetes OR hyperglycemia OR blood glucose",
    c2boolNotTitle: "NOT — Exclude (Remove Unwanted)",
    c2boolNotDesc: "Excludes results containing a term. Use carefully — NOT can accidentally exclude relevant studies.",
    c2boolNotExample: "cancer NOT animal NOT in vitro",
    c2s3ComboTitle: "Real Search Strategy: Combining AND + OR",
    c2s3ComboDesc: "Core principle: use OR to connect synonyms within each concept group (expand horizontally), use AND to connect different concepts (focus vertically). This is the fundamental architecture of a search strategy.",
    c2s3Next: "Understand PRISMA Screening →",

    // S4
    c2s4Label: "PRISMA Flow Diagram",
    c2s4Title: "The Roadmap of Screening",
    c2s4Intro: "The PRISMA 2020 flow diagram is the standard reporting tool for systematic reviews. It documents every step from search to final inclusion, showing readers your screening process and reasons for exclusion.",
    c2prismaTitle: "PRISMA 2020 Flow Diagram",
    c2prismaDesc: "Click 'Run Screening' to watch the animated flow, or click each step for details",
    c2prismaIdNum: "2,847",
    c2prismaIdLabel: "Identification: Database Search Results",
    c2prismaIdDesc: "The initial number of records from all databases. Record each database separately: PubMed 1,234 + Embase 987 + Cochrane 626 = 2,847 total.",
    c2prismaDupNum: "1,923",
    c2prismaDupLabel: "After Deduplication",
    c2prismaDupDesc: "Different databases often index the same articles. Use tools like EndNote, Covidence, or Rayyan to automatically identify and remove duplicates. This example removed 924 duplicates.",
    c2prismaDuplicatesExcl: "924 duplicates removed",
    c2prismaScreenNum: "412",
    c2prismaScreenLabel: "Screening: Title & Abstract",
    c2prismaScreenDesc: "Two independent reviewers read titles and abstracts, applying pre-set inclusion/exclusion criteria. Disagreements resolved by a third reviewer. Use tools like Rayyan or Covidence to track decisions.",
    c2prismaScreeningExcl: "1,511 irrelevant excluded",
    c2prismaEligNum: "48",
    c2prismaEligLabel: "Full-text Eligibility Assessment",
    c2prismaEligDesc: "Retrieve and assess full texts in detail. Record the specific reason for each exclusion (e.g., not an RCT, wrong population, outcome mismatch). PRISMA requires reporting exclusion reasons.",
    c2prismaEligibilityExcl: "364 excluded (with reasons)",
    c2prismaInclNum: "18",
    c2prismaInclLabel: "Included in Meta-Analysis",
    c2prismaInclDesc: "Studies passing all screening criteria that match your PICO are included in the quantitative meta-analysis. You may further distinguish studies included in qualitative vs. quantitative synthesis.",
    c2prismaRunBtn: "▶ Run Screening Animation",
    c2prismaResetBtn: "↺ Replay",
    c2prismaRunning: "Screening…",
    c2s4Next: "Screening Tips →",

    // S5
    c2s5Label: "Screening Tips",
    c2s5Title: "How to Screen Efficiently and Accurately?",
    c2s5Intro: "Screening thousands of articles sounds daunting, but these tips can dramatically improve your speed and accuracy.",
    c2tip1Title: "Pre-define Inclusion/Exclusion Criteria",
    c2tip1Desc: "Before screening, clearly define criteria based on your PICO and write them into a protocol. This reduces subjectivity and gives both reviewers consistent guidance.",
    c2tip2Title: "Two Independent Reviewers",
    c2tip2Desc: "At least two reviewers screen independently, then compare results. Calculate Cohen's kappa for agreement. Disagreements resolved by discussion or a third reviewer.",
    c2tip3Title: "Use Screening Tools",
    c2tip3Desc: "Rayyan, Covidence, and ASReview support blinded screening, conflict flagging, and decision tracking. Much more efficient than Excel and easier to document.",
    c2tip4Title: "When in Doubt, Include",
    c2tip4Desc: "During title/abstract screening, if you're unsure, mark it as 'maybe include.' Better to exclude at full-text stage than accidentally miss a relevant study at screening.",
    c2s5Next: "Challenge Yourself →",

    // Game
    c2gameLabel: "Search Strategy Game",
    c2gameTitle: "🔍 Literature Search Adventure",
    c2gameDesc: "Test your understanding of literature search and screening! Get questions right to make your dino buddy happy.",
    c2gamePickDino: "Pick a dino companion for your quest!",
    c2gameMaster: "Search Master! 🏆",
    c2gameGood: "Nice work! 👍",
    c2gameLearning: "Keep learning! 📚",
    c2gameScore: (score, total) => `${score} / ${total} correct`,
    c2gamePlayAgain: "Play Again",
    c2gameQ: (n, total) => `Question ${n} / ${total}`,
    c2gameNext: "Next →",
    c2gameResults: "See Results →",

    // AI Workshop
    c2aiLabel: "AI Search Workshop",
    c2aiTitle: "Let AI Help Build Your Search Strategy",
    c2aiDesc: "Enter your research topic and AI will help you break down search concepts, suggest keywords and MeSH terms, and generate a ready-to-use PubMed search syntax.",
    c2aiInstructions: "Paste your PubMed search syntax:",
    c2aiPlaceholder: "e.g., Effect of SGLT2 inhibitors on mortality in heart failure patients",
    c2aiGenerate: "Generate Search Strategy",

    // S3b: MeSH & Controlled Vocabulary
    c2s3bLabel: "Controlled Vocabulary",
    c2s3bTitle: "MeSH, Explosion & Truncation",
    c2s3bIntro: "Free-text searching alone isn't enough. Databases use controlled vocabularies (like PubMed's MeSH) to standardize indexing, ensuring articles on the same concept can be found. Your strategy should combine MeSH terms and free text for best coverage.",
    c2s3Next2: "Learn MeSH Terms →",

    // S6: Grey Literature
    c2s6Label: "Grey Literature",
    c2s6Title: "Beyond Databases: Grey Literature",
    c2s6Intro: "Searching only published literature isn't enough. Studies with significant results are more likely to be published (publication bias), so searching grey literature reduces bias and makes your review more complete and trustworthy.",
    c2s6Tip: "The Cochrane Handbook recommends searching at least one trial register (e.g., ClinicalTrials.gov or WHO ICTRP) and hand-searching reference lists of included studies.",
    c2s6Next: "Strategy Pitfalls →",

    // S7: Search Strategy Pitfalls
    c2s7Label: "Pitfalls",
    c2s7Title: "Common Search Strategy Pitfalls",
    c2s7Intro: "Even with correct Boolean logic and the right databases, search strategies can still have issues. Here are the most common pitfalls and best practices.",

    // Screening drill button
    c2s5Next2: "Learn Grey Literature →",

    // AI Workshop — Boolean Query Checker
    c2aiSectionTitle: "Let AI Review Your Search Syntax",
    c2aiSectionDesc: "Use AI to practice and refine your search strategies. These tools don't replace your professional judgment, but can help you build, review, and write strategies faster.",
    c2aiBDesc: "Paste your PubMed search syntax. AI will check Boolean logic, missing synonyms, and suggest improvements.",
    c2aiBPlaceholder: "e.g., (heart failure) AND (SGLT2 inhibitor OR dapagliflozin) AND (mortality)",
    c2aiBBtn: "AI Review My Query",
    c2aiBLoading: "Reviewing…",
    c2aiBResult: "AI Review Results",
    c2aiError: "Connection error. Please try again.",
    c2aiNoResult: "Could not get feedback.",
    c2checkAnswers: "Check Answers",
    c2tryAgain: "Try Again",
    c2correct: "correct",

    // ═══ COURSE 3: Data Extraction & Risk of Bias ═══
    c3Label: "Course 3",
    c3Title: "Data Extraction & Risk of Bias",
    c3Subtitle: "Data Extraction & RoB",
    c3Desc: "Precisely extract data from included studies and assess each study's quality. Like measuring ingredients and checking freshness before cooking — this is the key step to ensuring reliable meta-analysis results.",
    c3StartBtn: "Start Learning →",
    c3s1Label: "Why Does Data Extraction Matter?",
    c3s1Title: "Read the Nutrition Label, Not Just the Packaging",
    c3s1Intro: "Imagine grocery shopping: the package says 'healthy' and 'natural,' but what really matters are the numbers on the nutrition label — calories, protein, sodium. Data extraction works the same way: don't just read the abstract's conclusion; carefully record the raw data.",
    c3s1Bad: "❌ Bad Practice",
    c3s1BadEx: "Grabbing only p-values from abstracts: 'p = 0.03, significant difference'",
    c3s1BadWhy: "Missing sample sizes, event counts, effect sizes — like reading the 'healthy' label without checking the ingredients. Garbage in, garbage out.",
    c3s1Good: "✅ Good Practice",
    c3s1GoodEx: "Structured extraction: events 15/100 vs 22/100, mean±SD, study design, follow-up duration",
    c3s1GoodWhy: "Raw data lets meta-analysis software accurately calculate effect sizes and properly weight each study.",
    c3s1Next: "Let's see the standardized extraction form →",
    c3s2Label: "The Extraction Table",
    c3s2Title: "Your Standardized Extraction Form",
    c3s2Intro: "Just like a recipe requires precise measurements of each ingredient, data extraction needs a standardized form to ensure the same information is collected from every study. Click each column below to see what goes in it.",
    c3s2Tip: "Cooking analogy: The extraction form is your set of measuring cups and spoons — ensuring every 'recipe' is precisely consistent. Before formal extraction, pilot with 2-3 studies to calibrate your team.",
    c3s2Next: "Next: What numbers to extract →",
    c3s3Label: "What Numbers to Extract?",
    c3s3Title: "Dichotomous vs Continuous Outcomes",
    c3s3Intro: "Outcome data comes in two main types: dichotomous (yes/no, like mortality) and continuous (numerical, like blood pressure change). Each type requires extracting different numbers. Click below for details.",
    c3s3Next: "Learn why quality assessment matters →",
    c3s4Label: "Why Quality Matters",
    c3s4Title: "Risk of Bias: Checking Ingredient Freshness",
    c3s4Intro: "Not all included studies can be trusted. Like checking ingredient freshness before cooking — one spoiled ingredient can ruin the entire dish. One high-risk-of-bias study can distort the whole meta-analysis.",
    c3s4Analogy: "Imagine making soup with 5 ingredients. One is spoiled. Even if the other 4 are fresh, that one bad ingredient is enough to ruin the whole pot. Sensitivity analysis is like: removing the suspicious ingredient and checking if the soup still tastes different.",
    c3s4Next: "Meet the quality assessment tools →",
    c3s5Label: "Quality Assessment Tools",
    c3s5Title: "Two Key Quality Assessment Tools",
    c3s5Intro: "Different study types use different quality assessment tools. RCTs use Cochrane RoB 2 (traffic-light system), while observational studies use the Newcastle-Ottawa Scale (star system). Click the cards below for details.",
    c3s5Next: "Putting it all together →",
    c3s6Label: "Putting It Together",
    c3s6Title: "Traffic-Light Matrix & Sensitivity Analysis",
    c3s6Intro: "Arrange all studies' risk of bias assessments in a traffic-light table for an at-a-glance view. Then use sensitivity analysis to test: does the conclusion change when you remove high-risk studies?",
    c3s6Tip: "Key principle: Two people independently extract data and assess quality. Document disagreements and resolutions. Transparency and reproducibility are the cornerstones of systematic reviews.",
    c3s6Next: "Learn about dual extraction →",
    c3gameLabel: "Save Dino's Home 🏠",

    // AI Workshop — Extraction Reviewer
    c3aiLabel: "AI Extraction Workshop",
    c3aiTitle: "Let AI Review Your Data Extraction",
    c3aiError: "Connection error. Please try again.",
    c3aiNoResult: "Could not get feedback.",

    // ── Course 4: Effect Sizes & Forest Plots ──
    c4Label: "Course 4",
    c4Title: "Effect Sizes & Forest Plots",
    c4Subtitle: "Effect Sizes & Forest Plots",
    c4Desc: "A study says the drug 'works' — but how much? Learn to quantify effect magnitude, understand weighting methods, and read and build forest plots.",
    c4StartBtn: "Start Learning →",
    c4s1Label: "What Is an Effect Size?",
    c4s1Title: "Not Just 'It Works,' but 'How Much'",
    c4s1Intro: "A study tells you a drug 'works' — but what does that really mean? The effect size is that critical number: it quantifies the magnitude and direction of a finding. No more binary 'there is a difference' vs 'there isn't' — it's a precise score.",
    c4s1Analogy: "Think of an effect size like a restaurant's star rating on Yelp — not just 'recommend/don't recommend,' but a specific score. Would you trust a 4.5-star review or a vague 'it's okay'? The effect size is research's 'star rating.'",
    c4s1Next: "See the four key types →",
    c4s2Label: "Types of Effect Sizes",
    c4s2Title: "Four Key Effect Sizes",
    c4s2Intro: "Depending on outcome type (binary vs continuous) and whether scales are the same, we choose different effect sizes. Click each card below for formulas, uses, and pharmacy examples.",
    c4s2Next: "Next: Learn about weighting →",
    c4s3Label: "Weighting",
    c4s3Title: "Not All Studies Are Equal",
    c4s3Intro: "Imagine running a poll: would you trust 10,000 responses or 10? Meta-analysis works the same way — larger, more precise studies get a bigger 'vote.' This is the core logic of inverse-variance weighting.",
    c4s3Next: "Fixed vs Random Effects →",
    c4s4Label: "Fixed vs Random Effects",
    c4s4Title: "One True Effect, or Many?",
    c4s4Intro: "The fixed-effect model assumes all studies estimate the same true effect. The random-effects model acknowledges effects may genuinely differ across studies (different populations, doses, settings). Toggle the model below and watch the CI change.",
    c4s4Next: "Anatomy of a forest plot →",
    c4s5Label: "Forest Plot Anatomy",
    c4s5Title: "Building a Forest Plot Piece by Piece",
    c4s5Intro: "The forest plot is the 'business card' of a meta-analysis — it displays every study's effect, CI, weight, and the pooled result on one figure. Hover over each element below to learn what it represents.",
    c4s5Next: "Practice reading forest plots →",
    c4s6Label: "Reading Forest Plots",
    c4s6Title: "Three Practice Exercises",
    c4s6Intro: "Now that you know every element of a forest plot, let's actually read three practice plots with guided questions: Does the pooled effect cross the null? Which study has the most weight? Is the overall result significant?",
    c4s6Next: "Ready? Let's play the game! 🎮",
    c4gameLabel: "Dino Game 🎮",

    // ── Course 5: Heterogeneity & Publication Bias ──
    c5Label: "Course 5",
    c5Title: "Heterogeneity & Publication Bias",
    c5Subtitle: "Heterogeneity & Pub. Bias",
    c5Desc: "You combined 10 studies, but they disagree wildly — can you trust the average? Learn to assess between-study variation, detect publication bias, and properly report your meta-analysis.",
    c5StartBtn: "Start Learning →",
    c5s1Label: "What Is Heterogeneity?",
    c5s1Title: "Studies Disagree — Can You Trust the Average?",
    c5s1Intro: "You ran a meta-analysis combining 10 studies. But look closely — some show a large effect, some show nearly none, and some even point in opposite directions. This 'inconsistency' is heterogeneity. When it's high, the pooled effect may be as meaningless as averaging Arctic and Sahara temperatures.",
    c5s1Analogy: "Average the temperature of 5 cities: if one is in the Arctic and one is in the Sahara, the mean temperature represents neither — it's a meaningless number. A meta-analysis with high heterogeneity faces the same problem.",
    c5s1Next: "Quantify heterogeneity →",
    c5s2Label: "Measuring Heterogeneity",
    c5s2Title: "I², Q, and Prediction Intervals",
    c5s2Intro: "Three tools to measure heterogeneity: Cochran's Q test (is there more variation than expected by chance?), I² statistic (what percentage of variation is due to real differences?), and prediction interval (where would a future study's effect fall?). Drag the slider below to experience different I² levels.",
    c5s2Next: "Why does heterogeneity happen? →",
    c5s3Label: "Sources of Heterogeneity",
    c5s3Title: "Four Sources of Heterogeneity",
    c5s3Intro: "Heterogeneity doesn't appear randomly — it has causes. We need to find which factors explain the inconsistency. Clinical settings? Methodological choices? Statistical definitions? When heterogeneity is high, investigate why rather than just reporting I².",
    c5s3Next: "How to investigate →",
    c5s4Label: "Exploring Heterogeneity",
    c5s4Title: "Subgroup Analysis & Meta-Regression",
    c5s4Intro: "After finding heterogeneity, the next step is exploring why. Two main approaches: subgroup analysis (split studies by a factor and compare) and meta-regression (examine trends with continuous covariates). The demo below shows how splitting by dose 'unlocks' the heterogeneity mystery.",
    c5s4Next: "Meet publication bias →",
    c5s5Label: "Publication Bias",
    c5s5Title: "The 'Disappearing' Studies in the File Drawer",
    c5s5Intro: "What if studies with non-significant results are shelved in researchers' drawers, unpublished? Your meta-analysis only sees the 'successful' studies, systematically overestimating the effect. This is publication bias — and funnel plots can help detect it.",
    c5s5Tip: "Beyond funnel plots, there's Egger's test (statistical test for asymmetry) and trim-and-fill (imputes missing studies and recalculates the pooled effect). But all methods have limitations and need at least ~10 studies to be meaningful.",
    c5s5Next: "Final step: Proper reporting →",
    c5s6Label: "Reporting Your Analysis",
    c5s6Title: "PRISMA 2020 & Complete Reporting",
    c5s6Intro: "The 6-course journey reaches its final stop. A good meta-analysis isn't just about computing the pooled effect — it requires transparent, complete reporting of the entire process. The PRISMA 2020 checklist is your 'reporting compass.'",
    c5s6Tip: "Congratulations on completing the entire meta-analysis learning journey! From PICO to searching, screening, extraction, quality assessment, effect size pooling, heterogeneity exploration, bias detection, and reporting — that's a complete meta-analysis. Remember: meta-analysis is a powerful tool, but its quality depends on methodological rigor and your critical thinking.",
    c5s6Next: "Ready? Let's play the final game! 🎮",
    c5gameLabel: "Final Dino Game 🎮",
  },
};

const I18nContext = createContext();

// First visit: Chinese browsers get Chinese, all others get English.
const browserLang = () =>
  (navigator.language || "").toLowerCase().startsWith("zh") ? "zh" : "en";

export function I18nProvider({ children }) {
  const [lang, setLang] = useState(() => {
    try {
      return localStorage.getItem("ma101-lang") || browserLang();
    } catch {
      return browserLang();
    }
  });

  const toggleLang = useCallback(() => {
    setLang((prev) => {
      const next = prev === "zh" ? "en" : "zh";
      try { localStorage.setItem("ma101-lang", next); } catch {}
      return next;
    });
  }, []);

  const t = useCallback(
    (key, ...args) => {
      const val = translations[lang][key];
      if (typeof val === "function") return val(...args);
      return val || key;
    },
    [lang]
  );

  return (
    <I18nContext.Provider value={{ lang, toggleLang, t }}>
      {children}
    </I18nContext.Provider>
  );
}

export function useI18n() {
  return useContext(I18nContext);
}
